// PsyGestion — Dashboard principal — Taux officiels 2026

import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { LiveNumber } from "@/components/LiveNumber";
import { BarChart3, FileText, LogOut, Settings, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/hooks/useSubscription";
import PaywallCard from "@/components/PaywallCard";
import { toast } from "@/hooks/use-toast";
import {
  Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line
} from "recharts";
import Simulateur from "@/components/Simulateur";
import Projection from "@/components/Projection";
import AssistantChat from "@/components/AssistantChat";
import AvatarDropdown from "@/components/AvatarDropdown";
import { PsyGestionIcon, PsyGestionSpinner } from "@/components/PsyGestionLogo";
import { usePageTitle } from "@/hooks/usePageTitle";
import Charges2035 from "@/components/Charges2035";
import DataImporter from "@/components/DataImporter";
import {
  calculIR, calculURSSAF_micro, calculURSSAF_bnc, calculBeneficeMicro,
  calculCotisationsTotal_micro,
  calculProvisionSociale, calculProvisionFiscale,
  TAUX_TOTAL_MICRO_CIPAV, ABATTEMENT_MICRO_BNC,
  SEUIL_MICRO_BNC, SEUIL_TVA_FRANCHISE, SEUIL_TVA_MAJORE,
  DISCLAIMER_ARAPL, DISCLAIMER_EXPERT,
  hasRecettesSoumisesTVA, calculCASoumiseTVA,
} from "@/utils/fiscal";
import ProgressionAnnuelle from "@/components/ProgressionAnnuelle";
import { useNavigation } from "@/contexts/NavigationContext";
import CalendrierFiscal from "@/components/CalendrierFiscal";
import FoundingBanner from "@/components/FoundingBanner";
import QuickCatchupBanner from "@/components/QuickCatchupBanner";
import DevDebugPanel from "@/components/DevDebugPanel";

// ─── CONSTANTES OFFICIELLES 2026 — CIPAV (psychologues) ──────────────────────
const TAUX_URSSAF = TAUX_TOTAL_MICRO_CIPAV;  // 23,4% CIPAV (23,2% + 0,2% CFP)
const SEUIL_CA = SEUIL_MICRO_BNC;            // 83 600€ (2026)
const SEUIL_TVA = SEUIL_TVA_FRANCHISE; // 37 500€ (2026)
const SEUIL_ALERTE = 75000;
const ABATTEMENT = ABATTEMENT_MICRO_BNC;
const TAUX_URSSAF_BNC = 0.40; // ~40% estimation BNC classique (taux moyen constaté 35-45% du bénéfice net)
const PROVISION_IR = 0.15; // 15% provision IR prudente
const PROVISION_TOTALE = TAUX_URSSAF + PROVISION_IR; // 38,4% — Micro-BNC CIPAV
const PROVISION_BNC = 0.55; // BNC classique : ~40% cotisations + 15% IR sur bénéfice net

const DISCLAIMER_MICRO = `Calculs basés sur les taux officiels — Cotisations CIPAV : 23,2% du CA + 0,2% CFP = 23,4% — Abattement Micro-BNC : 34% — Seuil Micro-BNC 2026-2028 : 83 600€ — Seuil TVA franchise : 37 500€. ${DISCLAIMER_ARAPL} ${DISCLAIMER_EXPERT}`;
const DISCLAIMER_BNC = `Cotisations sociales BNC classique estimées entre 35% et 45% du bénéfice net selon votre niveau de revenus (fourchette prudente). Vos cotisations réelles sont calculées par l'URSSAF sur votre bénéfice net déclaré — consultez urssaf.fr pour votre montant exact. ${DISCLAIMER_ARAPL} ${DISCLAIMER_EXPERT}`;

const MOIS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
const MOIS_FULL = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const TRIMESTRES = [
  { label: "T1", mois: [0,1,2], deadline: "30 avril" },
  { label: "T2", mois: [3,4,5], deadline: "31 juillet" },
  { label: "T3", mois: [6,7,8], deadline: "31 octobre" },
  { label: "T4", mois: [9,10,11], deadline: "31 janvier N+1" },
];

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", blue: "#2E6DA4",
  light: "#EAF2FB", lightTeal: "#E8F5F4", danger: "#E63946",
  warning: "#F4A261", success: "#2A9D8F", muted: "#6B85A3",
  border: "#D6E4F0", bg: "#F4F8FC", white: "#FFFFFF", purple: "#7C3AED",
};

const fmt = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmtDec = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(n);
const fmtEur = (v: number) => `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(v)} €`;

const today = new Date();
const currentYear = today.getFullYear();
const currentMonth = today.getMonth();
const currentQuarter = Math.floor(currentMonth / 3);

// ─── COMPOSANTS UI ────────────────────────────────────────────────────────────
const Card = ({ children, className = "", style = {} }: { children?: React.ReactNode; className?: string; style?: React.CSSProperties }) => (
  <div className={`rounded-2xl bg-white shadow-sm border p-5 ${className}`} style={{ borderColor: C.border, ...style }}>
    {children}
  </div>
);

const StatCard = ({ label, value, rawValue, sub, color, icon, highlight, pulse }: { label: string; value: string; rawValue?: number; sub?: string; color?: string; icon?: string; highlight?: string; pulse?: boolean }) => (
  <Card className="flex flex-col gap-1">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.muted }}>{label}</span>
      <span className="text-lg">{icon}</span>
    </div>
    <div className="text-2xl font-bold" style={{ color: color || C.navy }}>
      {rawValue !== undefined ? <LiveNumber value={rawValue} fmt={fmt} baseColor={color || C.navy} /> : value}
    </div>
    {sub && <div className="text-xs mt-1" style={{ color: C.muted }}>{sub}</div>}
    {highlight && <div className="text-xs font-medium mt-1 px-2 py-0.5 rounded" style={{ backgroundColor: C.light, color: C.blue }}>{highlight}</div>}
  </Card>
);

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl shadow-lg p-3 text-sm" style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}>
      {label && <div className="font-semibold mb-1" style={{ color: C.navy }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.fill || p.color }} />
          <span style={{ color: C.muted }}>{p.name} :</span>
          <span className="font-bold" style={{ color: C.navy }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const PrelevementSourceCard = ({ ca, parMois, chargesDeduites, isMicroBNC, onNavigateCharges }: { ca: number; parMois: { ca: number }[]; chargesDeduites: number; isMicroBNC: boolean; onNavigateCharges?: () => void }) => {
  const [pasTooltip, setPasTooltip] = React.useState(false);

  // BNC classique sans charges saisies
  const showChargesPrompt = !isMicroBNC && chargesDeduites === 0;

  // Extrapolation
  const moisActifs = parMois.filter(m => m.ca > 0).length;
  const caAnnuel = (ca >= 10000 || moisActifs >= 3) ? ca : (moisActifs > 0 ? (ca / moisActifs) * 12 : 0);

  // Revenu imposable selon le régime
  const urssafEstimee = Math.max(0, caAnnuel - chargesDeduites) * 0.40;
  const revenuImposable = isMicroBNC
    ? caAnnuel * 0.66
    : Math.max(0, caAnnuel - chargesDeduites - urssafEstimee);

  const irAnnuel = calculIR(revenuImposable);
  const acompteMensuel = Math.round(irAnnuel / 12);

  const statusColor = acompteMensuel < 200 ? C.teal : acompteMensuel <= 500 ? C.warning : C.danger;
  const statusIcon = acompteMensuel < 200 ? "✅" : acompteMensuel <= 500 ? "⚠️" : "🔴";
  const statusText = acompteMensuel < 200
    ? "Votre charge fiscale mensuelle est maîtrisée"
    : acompteMensuel <= 500
      ? `Pensez à provisionner ${fmtEur(acompteMensuel)} supplémentaires par mois pour votre IR`
      : "Charge fiscale élevée — vérifiez votre taux sur impots.gouv.fr";

  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-bold text-sm" style={{ color: C.navy }}>💸 Impôts prélevés chaque mois</span>
        <span className="relative inline-block">
          <button onClick={() => setPasTooltip(!pasTooltip)} className="text-sm cursor-pointer" title="Info">❓</button>
          {pasTooltip && (
            <div className="absolute left-6 top-0 z-20 w-72 p-3 rounded-xl shadow-lg text-xs border"
              style={{ backgroundColor: C.white, borderColor: C.border, color: C.navy }}>
              Le prélèvement à la source est un acompte mensuel prélevé directement par les impôts. Il est calculé sur votre revenu de l'année précédente. Si votre activité évolue significativement, ajustez votre taux sur impots.gouv.fr pour éviter une régularisation surprise en fin d'année.
              <button onClick={() => setPasTooltip(false)} className="block mt-1 text-[10px] underline" style={{ color: C.muted }}>Fermer</button>
            </div>
          )}
        </span>
      </div>

      {showChargesPrompt ? (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">📋</span>
            <span className="font-bold text-sm" style={{ color: C.navy }}>Saisissez vos charges pour obtenir votre estimation IR</span>
          </div>
          <p className="text-[13px] mb-3" style={{ color: C.muted }}>
            En BNC classique, votre IR dépend de vos charges déductibles réelles. Renseignez vos charges dans l'onglet Charges 2035 pour obtenir une estimation précise.
          </p>
          <button
            onClick={onNavigateCharges}
            className="inline-block text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80"
            style={{ borderColor: C.teal, color: C.teal }}
          >
            → Saisir mes charges
          </button>
        </div>
      ) : (
        <>
          <div className="text-[28px] font-bold mb-1" style={{ color: C.teal }}>
            Acompte IR estimé : {fmtEur(acompteMensuel)}/mois
          </div>
          <div className="text-[13px] mb-3" style={{ color: C.muted }}>
            Prélevé automatiquement par les impôts sur votre compte bancaire chaque mois
          </div>
          <div className="border-t mb-3" style={{ borderColor: C.border }} />
          <div className="text-[13px] font-medium flex items-center gap-1.5 mb-3" style={{ color: statusColor }}>
            <span>{statusIcon}</span> {statusText}
          </div>
          <a
            href="https://www.impots.gouv.fr/gerer-mon-prelevement-la-source-0"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:opacity-80"
            style={{ borderColor: C.teal, color: C.teal }}
          >
            ⚙️ Ajuster mon taux sur impots.gouv.fr
          </a>
        </>
      )}

      <p className="text-[11px] italic mt-3" style={{ color: C.muted }}>
        Estimation basée sur votre CA actuel extrapolé — le montant réel est calculé par les impôts sur votre déclaration annuelle.
      </p>
    </Card>
  );
};

// ─── POINT MORT CARD ─────────────────────────────────────────────────────────
const PointMortCard = ({
  ca,
  chargesDeduites,
  isMicroBNC,
  tarifSeance,
  onNavigateProfil,
}: {
  ca: number;
  chargesDeduites: number;
  isMicroBNC: boolean;
  tarifSeance: number | null;
  onNavigateProfil: () => void;
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const tarif = tarifSeance ?? 70;
  const semaines = 44;

  const netParN = (n: number) => {
    const caN = n * tarif * semaines;
    if (isMicroBNC) {
      return caN - calculURSSAF_micro(caN) - calculIR(calculBeneficeMicro(caN));
    }
    const ben = Math.max(0, caN - chargesDeduites);
    const urssaf = calculURSSAF_bnc(ben);
    return Math.round(caN - chargesDeduites - urssaf - calculIR(Math.max(0, ben - urssaf)));
  };

  // Dichotomie : nb minimum de patients pour net > 0
  let lo = 1, hi = 200;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    netParN(mid) > 0 ? (hi = mid) : (lo = mid + 1);
  }
  const pointMort = lo;

  const startOfYear = new Date(currentYear, 0, 1);
  const semainesEcoulees = Math.max(1, Math.floor((today.getTime() - startOfYear.getTime()) / (7 * 24 * 3600 * 1000)));
  const nbActuel = tarif > 0 ? Math.round(ca / tarif / semainesEcoulees) : 0;

  const revenuParPatientSuppMensuel = Math.round(
    (netParN(nbActuel + 1) - netParN(nbActuel)) / 12
  );

  const insufficient = ca < 500 || !tarifSeance;

  if (insufficient) {
    return (
      <Card style={{ backgroundColor: C.bg }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.muted }}>Seuil de rentabilité</span>
          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: C.border, color: C.muted }}>📊 Configurez votre profil</span>
        </div>
        <p className="text-sm text-center py-2" style={{ color: C.navy }}>
          Renseignez votre tarif pour voir le nombre de séances nécessaires
        </p>
        <button
          onClick={onNavigateProfil}
          className="w-full py-2.5 rounded-xl text-white font-bold text-sm mt-2 hover:opacity-90 transition-opacity"
          style={{ backgroundColor: C.teal }}
        >
          → Configurer en 30 secondes
        </button>
      </Card>
    );
  }

  const isAbove = nbActuel > pointMort;
  const diff = nbActuel - pointMort;

  return (
    <Card className="relative">
      {/* Badge + tooltip */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {isAbove ? (
          <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: "#ECFDF5", color: "#059669" }}>✅ Rentable</span>
        ) : (
          <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: "#FFF7ED", color: C.warning }}>⚡ En développement</span>
        )}
        <span className="relative inline-block">
          <button
            onClick={() => setShowTooltip(!showTooltip)}
            className="w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center hover:opacity-80"
            style={{ backgroundColor: C.teal, color: C.white }}
          >?</button>
          {showTooltip && (
            <div className="absolute right-0 top-6 z-20 w-64 p-3 rounded-xl shadow-lg text-xs border"
              style={{ backgroundColor: C.white, borderColor: C.border, color: C.navy }}>
              Le nombre de patients à partir duquel vos revenus couvrent toutes vos charges et cotisations.
              <button onClick={() => setShowTooltip(false)} className="block mt-1 text-[10px] underline" style={{ color: C.muted }}>Fermer</button>
            </div>
          )}
        </span>
      </div>

      <div className="text-xs font-semibold uppercase tracking-widest mb-4 pr-32" style={{ color: C.muted }}>Rentabilité de votre cabinet</div>

      {isAbove ? (
        <div className="space-y-3">
          {/* Phrase narrative principale */}
          <p className="text-sm leading-relaxed" style={{ color: C.navy }}>
            Vous avez <strong style={{ color: C.teal }}>{nbActuel} patients actifs</strong> — il en faut{" "}
            <strong>{pointMort}</strong> minimum pour couvrir toutes vos charges et cotisations.
          </p>

          {/* Barre de progression visuelle */}
          <div>
            <div className="flex justify-between text-[11px] mb-1" style={{ color: C.muted }}>
              <span>0 patient</span>
              <span>Seuil : {pointMort}</span>
              <span>Vous : {nbActuel}</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: C.border }}>
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.min(100, Math.round((nbActuel / (nbActuel + 2)) * 100))}%`,
                  backgroundColor: C.teal,
                }}
              />
            </div>
          </div>

          {/* Insight monétaire — le vrai "so what" */}
          <div className="rounded-xl px-4 py-3" style={{ backgroundColor: `${C.teal}12`, border: `1px solid ${C.teal}30` }}>
            <p className="text-sm font-bold" style={{ color: C.teal }}>
              Chaque nouveau patient vous rapporte{" "}
              <span style={{ fontSize: 18 }}>+{fmt(revenuParPatientSuppMensuel)}</span> nets/mois de plus.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Phrase narrative principale */}
          <p className="text-sm leading-relaxed" style={{ color: C.navy }}>
            Il vous faut <strong style={{ color: C.warning }}>{pointMort} patients minimum</strong> pour couvrir
            vos charges et cotisations. Vous en avez <strong>{nbActuel}</strong> actuellement.
          </p>

          {/* Barre de progression */}
          <div>
            <div className="flex justify-between text-[11px] mb-1" style={{ color: C.muted }}>
              <span>0 patient</span>
              <span>Vous : {nbActuel}</span>
              <span>Objectif : {pointMort}</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: C.border }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(95, Math.round((nbActuel / pointMort) * 100))}%`,
                  backgroundColor: C.warning,
                }}
              />
            </div>
          </div>

          {/* CTA actionnable */}
          <div className="rounded-xl px-4 py-3" style={{ backgroundColor: "#FFF7ED", border: "1px solid #FED7AA" }}>
            <p className="text-sm font-bold" style={{ color: C.warning }}>
              Il vous manque <span style={{ fontSize: 18 }}>{Math.max(0, pointMort - nbActuel)} patient{pointMort - nbActuel > 1 ? "s" : ""}</span>{" "}
              pour être rentable — soit {fmt(Math.max(0, pointMort - nbActuel) * tarif * 44 / 12)}/mois de CA supplémentaire.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

// ─── APP PRINCIPALE ──────────────────────────────────────────────────────────
export default function Dashboard() {
  usePageTitle("Tableau de bord");
  const { user, signOut } = useAuth();
  const { profile, refetch: refetchProfile } = useProfile();
  const { isTrial, trialUsed, plan, status, isActive } = useSubscription();
  const proCta = trialUsed ? "Passer au Pro →" : "Passer au Pro — 14 jours gratuits →";
  const isPro = plan === "pro";
  const isProActive = isPro && status === "active";
  const isEssentiel = !isPro;
  const isMicroBNC = profile?.statut_fiscal !== "bnc-classique";
  const isDebutante = profile?.anciennete === "debutante";
  const isEtablie = profile?.anciennete === "etablie";
  const [recettes, setRecettes] = useState([]);
  const [objectifCA, setObjectifCA] = useState(50000);
  const [objectifInput, setObjectifInput] = useState("50000");
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ date: today.toISOString().slice(0, 10), montant: "", description: "" });
  const [formError, setFormError] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [filterMois, setFilterMois] = useState("all");
  const [saveStatus, setSaveStatus] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [chargesReelles, setChargesReelles] = useState("");
  const [chargesReellesInput, setChargesReellesInput] = useState("");
  const [chargesFromDB, setChargesFromDB] = useState(0);
  const [showDetails, setShowDetails] = useState(() => localStorage.getItem("psygestion_details_open") === "true");
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const { mode, setMode } = useNavigation();
  const [simulPreFill, setSimulPreFill] = useState<{ charges: Partial<Record<string, number>>; quickMode: boolean } | null>(null);

  // Séances state (persisted in Supabase)
  const [seancesMois, setSeancesMois] = useState("");
  const [dureeMoyenne, setDureeMoyenne] = useState("60");

  // Import insight (InsightFlash post-import)
  const [importInsight, setImportInsight] = useState<{ totalRecettes: number; totalCharges: number; nbRecettes: number } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Saisie par lot
  const [saisieMode, setSaisieMode] = useState<"lot" | "csv">("lot");
  const [manualOpen, setManualOpen] = useState(false);
  const [lotSeances, setLotSeances] = useState("");
  const [lotTarif, setLotTarif] = useState("");

  const [allSeances, setAllSeances] = useState<{mois: number; annee: number; nombre: number; duree: number; id: string}[]>([]);

  // Total séances annuelles
  const seancesAnnuelles = useMemo(() => {
    return allSeances.filter(s => s.annee === currentYear).reduce((sum, s) => sum + s.nombre, 0);
  }, [allSeances]);

  // Données d'onboarding pour projections fantômes
  const onboardingData = useMemo(() => {
    const stored = localStorage.getItem("psygestion_onboarding");
    const fallback = stored ? JSON.parse(stored) : {};
    const targetCa = (profile as any)?.target_monthly_ca ?? fallback.target_monthly_ca ?? 0;
    const estCharges = (profile as any)?.estimated_monthly_charges ?? fallback.estimated_monthly_charges ?? 0;
    const avgRate = (profile as any)?.average_session_rate ?? fallback.average_session_rate ?? 0;
    return { targetCa: Number(targetCa), estCharges: Number(estCharges), avgRate: Number(avgRate) };
  }, [profile]);

  // CA estimé depuis le profil (onboarding) quand aucune recette saisie
  const caEstime = useMemo(() => {
    if (onboardingData.targetCa > 0) return onboardingData.targetCa * 12;
    if (!profile?.tarif_seance || !profile?.nb_patients_semaine) return 0;
    return profile.tarif_seance * profile.nb_patients_semaine * (profile.semaines_travaillees ?? 45);
  }, [profile?.tarif_seance, profile?.nb_patients_semaine, profile?.semaines_travaillees, onboardingData.targetCa]);

  // Mode prévisionnel : CA réel < 10% de l'objectif annuel
  const caReelTotal = useMemo(() => {
    return recettes.filter(r => new Date(r.date).getFullYear() === currentYear).reduce((s, r) => s + Number(r.montant), 0);
  }, [recettes]);

  const hasRealData = caReelTotal > 0;

  const moisAvecRecettesCount = useMemo(() => {
    const moisSet = new Set<number>();
    recettes.filter(r => new Date(r.date).getFullYear() === currentYear).forEach(r => moisSet.add(new Date(r.date).getMonth()));
    return moisSet.size;
  }, [recettes]);

  const hasFullYear = moisAvecRecettesCount === 12;

  const isModePrevisionnel = useMemo(() => {
    const objectifAnnuel = onboardingData.targetCa * 12;
    return objectifAnnuel > 0 && caReelTotal < objectifAnnuel * 0.1;
  }, [caReelTotal, onboardingData.targetCa]);

  // Pré-remplissage saisie par lot depuis le profil et les séances existantes
  useEffect(() => {
    if (profile?.tarif_seance && !lotTarif) setLotTarif(String(profile.tarif_seance));
  }, [profile?.tarif_seance]);
  useEffect(() => {
    if (seancesMois && !lotSeances) setLotSeances(seancesMois);
  }, [seancesMois]);

  // Fermeture du menu Plus au clic extérieur
  useEffect(() => {
    if (!showPlusMenu) return;
    const handler = (e: MouseEvent) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target as Node)) {
        setShowPlusMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPlusMenu]);

  // ─── PERSISTANCE SUPABASE ──────────────────────────────────────────────────
  // Load charges from depenses_2035 (utilisé aussi après une mise à jour des charges)
  const loadChargesFromDB = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("depenses_2035")
      .select("montant")
      .eq("user_id", user.id)
      .eq("annee", currentYear)
      .limit(500);
    if (!error && data) {
      const total = data.reduce((s, d) => s + Number(d.montant), 0);
      setChargesFromDB(total);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      const [recettesResult, objectifResult, seancesResult, chargesResult] = await Promise.allSettled([
        supabase
          .from("recettes")
          .select("*")
          .eq("user_id", user.id)
          .gte("date", `${currentYear}-01-01`)
          .lte("date", `${currentYear}-12-31`)
          .limit(500),
        supabase
          .from("objectif")
          .select("*")
          .eq("user_id", user.id)
          .limit(1)
          .maybeSingle(),
        supabase
          .from("seances")
          .select("*")
          .eq("user_id", user.id)
          .limit(500),
        supabase
          .from("depenses_2035")
          .select("montant")
          .eq("user_id", user.id)
          .eq("annee", currentYear)
          .limit(500),
      ]);

      let hasError = false;

      if (recettesResult.status === "fulfilled" && !recettesResult.value.error) {
        const recettesData = recettesResult.value.data;
        if (recettesData) setRecettes(recettesData.map((r: any) => ({ id: r.id, date: r.date, montant: r.montant, description: r.description })));
      } else {
        hasError = true;
      }

      if (objectifResult.status === "fulfilled" && !objectifResult.value.error) {
        const objectifData = objectifResult.value.data;
        if (objectifData) {
          setObjectifCA(objectifData.valeur);
          setObjectifInput(String(objectifData.valeur));
        }
      } else {
        hasError = true;
      }

      if (seancesResult.status === "fulfilled" && !seancesResult.value.error) {
        const seancesData = seancesResult.value.data;
        if (seancesData) {
          setAllSeances(seancesData.map((s: any) => ({ id: s.id, mois: s.mois, annee: s.annee, nombre: s.nombre, duree: s.duree })));
          const current = seancesData.find((s: any) => s.mois === currentMonth && s.annee === currentYear);
          if (current) {
            setSeancesMois(String(current.nombre));
            setDureeMoyenne(String(current.duree));
          }
        }
      } else {
        hasError = true;
      }

      if (chargesResult.status === "fulfilled" && !chargesResult.value.error) {
        const chargesData = chargesResult.value.data;
        if (chargesData) {
          const total = chargesData.reduce((s: number, d: any) => s + Number(d.montant), 0);
          setChargesFromDB(total);
        }
      } else {
        hasError = true;
      }

      if (hasError) {
        toast({ title: "❌ Erreur de chargement", description: "Certaines données n'ont pas pu être chargées.", variant: "destructive", duration: 5000 });
      }

      setLoaded(true);
    };
    loadData();
  }, [user]);

  const saveObjectif = useCallback(async (val: number) => {
    if (!user) return;
    const { data, error: selErr } = await supabase.from("objectif").select("id").limit(1).maybeSingle();
    if (selErr) { toast({ title: "❌ Erreur", description: selErr.message, variant: "destructive" }); return; }
    if (data) {
      const { error } = await supabase.from("objectif").update({ valeur: val }).eq("id", data.id);
      if (error) toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
      else toast({ title: "✅ Objectif enregistré", duration: 2000 });
    } else {
      const { error } = await supabase.from("objectif").insert({ valeur: val, user_id: user.id });
      if (error) toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
      else toast({ title: "✅ Objectif enregistré", duration: 2000 });
    }
  }, [user]);

  const saveSeances = useCallback(async (nombre: number, duree: number) => {
    if (!user) return;
    const existing = allSeances.find(s => s.mois === currentMonth && s.annee === currentYear);
    if (existing) {
      const { error } = await supabase.from("seances").update({ nombre, duree }).eq("id", existing.id);
      if (error) toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
      else { setAllSeances(prev => prev.map(s => s.id === existing.id ? { ...s, nombre, duree } : s)); }
    } else {
      const { data, error } = await supabase.from("seances").insert({ user_id: user.id, mois: currentMonth, annee: currentYear, nombre, duree }).select().single();
      if (error) toast({ title: "❌ Erreur", description: error.message, variant: "destructive" });
      else if (data) { setAllSeances(prev => [...prev, { id: data.id, mois: currentMonth, annee: currentYear, nombre, duree }]); }
    }
  }, [user, allSeances]);

  // ─── CALCULS ──────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const recettesAnnee = recettes.filter(r => new Date(r.date).getFullYear() === currentYear);
    const caReel = recettesAnnee.reduce((s, r) => s + Number(r.montant), 0);
    const isEstimation = caReel === 0 && caEstime > 0;
    const ca = isEstimation ? caEstime : caReel;
    const chargesDeduites = isMicroBNC ? 0 : (chargesFromDB > 0 ? chargesFromDB : (Number(chargesReelles) || 0));
    
    // Cotisations CIPAV : Micro = CA × 23,4% | BNC classique = bénéfice net × ~40% (estimation taux moyen)
    const beneficeNetBNC = Math.max(0, ca - chargesDeduites);
    const urssaf = isMicroBNC
      ? ca * TAUX_URSSAF
      : Math.round(beneficeNetBNC * TAUX_URSSAF_BNC);
    
    // Bénéfice imposable (pour calcul IR)
    const beneficeImposable = isMicroBNC ? ca * (1 - ABATTEMENT) : Math.max(0, beneficeNetBNC - urssaf);
    const ir = calculIR(beneficeImposable);

    // Revenu net avant IR
    const revenuNetAvantIR = ca - urssaf - chargesDeduites;
    const revenuNet = revenuNetAvantIR - ir;
    
    const caRestant = Math.max(0, SEUIL_CA - ca);
    const pctSeuil = Math.min(100, (ca / SEUIL_CA) * 100);
    const pctObjectif = objectifCA > 0 ? Math.min(100, (ca / objectifCA) * 100) : 0;

    const parMois = Array.from({ length: 12 }, (_, i) => ({
      mois: MOIS[i], moisFull: MOIS_FULL[i], ca: 0, urssaf: 0, net: 0,
      projection: onboardingData.targetCa > 0 ? onboardingData.targetCa : 0,
      isProjection: false,
    }));
    recettesAnnee.forEach(r => {
      const m = new Date(r.date).getMonth();
      parMois[m].ca += Number(r.montant);
    });
    parMois.forEach(m => {
      m.isProjection = m.ca === 0 && m.projection > 0;
      if (m.ca > 0) m.projection = 0; // Hide ghost bar when real data exists
      const mBeneficeNet = Math.max(0, m.ca - (chargesDeduites * (ca > 0 ? m.ca / ca : 0)));
      m.urssaf = isMicroBNC
        ? m.ca * TAUX_URSSAF
        : Math.round(mBeneficeNet * TAUX_URSSAF_BNC);
      if (isMicroBNC) {
        m.net = m.ca - m.urssaf;
      } else {
        const ratio = ca > 0 ? m.ca / ca : 0;
        m.net = m.ca - (chargesDeduites * ratio) - m.urssaf;
      }
    });

    const cumulMensuel = [];
    let cumul = 0;
    parMois.forEach((m, i) => {
      cumul += m.ca;
      cumulMensuel.push({ mois: MOIS[i], cumul, objectif: objectifCA / 12 * (i + 1), seuil: SEUIL_CA / 12 * (i + 1) });
    });

    const moisActifs = parMois.filter(m => m.ca > 0);
    const moyenneMensuelle = moisActifs.length > 0 ? ca / moisActifs.length : 0;
    const moisRestants = 12 - currentMonth;
    const projectionFin = ca + moyenneMensuelle * moisRestants;
    const beneficeNetMensuel = Math.max(0, moyenneMensuelle - chargesDeduites);
    const provisionMensuelle = isMicroBNC
      ? moyenneMensuelle * PROVISION_TOTALE
      : Math.round(beneficeNetMensuel * PROVISION_BNC);

    const parTrimestre = TRIMESTRES.map((t, i) => {
      const caTrim = t.mois.reduce((s, m) => s + parMois[m].ca, 0);
      const beneficeNetTrim = Math.max(0, caTrim - (chargesDeduites * (ca > 0 ? caTrim / ca : 0)));
      const urssafTrim = isMicroBNC
        ? caTrim * TAUX_URSSAF
        : Math.round(beneficeNetTrim * TAUX_URSSAF_BNC);
      return { ...t, ca: caTrim, urssaf: urssafTrim, estCourant: i === currentQuarter };
    });

    return { ca, caReel, isEstimation, urssaf, revenuNet, revenuNetAvantIR, beneficeImposable, ir, caRestant, pctSeuil, pctObjectif, parMois, cumulMensuel, moyenneMensuelle, projectionFin, provisionMensuelle, recettesAnnee, parTrimestre, chargesDeduites };
  }, [recettes, objectifCA, isMicroBNC, chargesReelles, chargesFromDB, caEstime]);

  // ─── CONSEILS ─────────────────────────────────────────────────────────────
  const conseil = useMemo(() => {
    const { ca } = stats;
    if (!isMicroBNC) {
      // BNC classique conseils
      if (ca < 20000) return { level: "green", icon: "📊", titre: "BNC Classique — Début d'année", texte: `CA de ${fmt(ca)}. Pensez à bien comptabiliser toutes vos charges déductibles (loyer, supervision, formations, assurances…).` };
      if (ca < 50000) return { level: "green", icon: "✅", titre: "BNC Classique — Bonne progression", texte: `CA de ${fmt(ca)}. Vérifiez régulièrement vos charges déductibles avec votre comptable pour optimiser votre résultat.` };
      return { level: "orange", icon: "💡", titre: "BNC Classique — Optimisation", texte: `CA de ${fmt(ca)}. Pensez au contrat Madelin retraite et aux investissements déductibles pour optimiser votre fiscalité.` };
    }
    if (ca < SEUIL_TVA) return { level: "green", icon: "✅", titre: "Situation normale — Franchise TVA active", texte: `CA de ${fmt(ca)} — sous le seuil TVA (${fmt(SEUIL_TVA)}). Continuez à mentionner « TVA non applicable — art. 293B du CGI » sur vos factures.` };
    if (ca < SEUIL_TVA_MAJORE) return { level: "orange", icon: "⚠️", titre: "Attention — Seuil TVA franchi", texte: `Vous approchez du seuil TVA — au-delà de ${fmt(SEUIL_TVA_MAJORE)} vous devrez facturer la TVA à vos patients.` };
    if (ca < SEUIL_ALERTE) return { level: "orange", icon: "🚨", titre: "Seuil TVA majoré dépassé", texte: `Seuil TVA majoré dépassé — vous devez facturer la TVA dès ce mois, consultez un expert-comptable.` };
    return { level: "red", icon: "🚨", titre: "DANGER — Seuil critique approché", texte: `CA de ${fmt(ca)} — proche du plafond Micro-BNC (${fmt(SEUIL_CA)}). Consultez un expert-comptable immédiatement.` };
  }, [stats, isMicroBNC]);

  const cc = { green: { bg: C.lightTeal, border: C.teal, text: C.teal }, orange: { bg: "#FFF8F0", border: C.warning, text: "#C46200" }, red: { bg: "#FFF0F1", border: C.danger, text: C.danger } }[conseil.level];

  // ─── SAISIE ───────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!form.montant || isNaN(Number(form.montant)) || Number(form.montant) <= 0) { setFormError("Montant invalide."); return; }
    if (!form.date) { setFormError("Date requise."); return; }
    if (!user) { setFormError("Non authentifié."); return; }
    setFormError("");
    const newRecette = { date: form.date, montant: Number(form.montant), description: form.description || "Recette", user_id: user.id };
    const montantSaisi = Number(form.montant);
    const { data, error } = await supabase.from("recettes").insert(newRecette).select().single();
    if (error) {
      setFormError(`Erreur : ${error.message}`);
      toast({ title: "❌ Erreur lors de l'enregistrement", description: "Vérifiez votre connexion.", variant: "destructive", duration: 5000 });
      // Retry once after 3s
      setTimeout(async () => {
        const { error: retryErr } = await supabase.from("recettes").insert(newRecette).select().single();
        if (!retryErr) { toast({ title: "✅ Recette enregistrée après réessai", duration: 3000 }); }
      }, 3000);
      return;
    }
    setRecettes(prev => [...prev, { id: data.id, date: data.date, montant: data.montant, description: data.description }]);
    setForm(f => ({ ...f, montant: "", description: "" }));
    setSaveStatus("✓ Sauvegardé");
    
    setTimeout(() => setSaveStatus(""), 2000);
    toast({ title: `✅ Recette de ${fmt(montantSaisi)} enregistrée`, description: "Tableau de bord mis à jour.", duration: 3000 });
  };

  const handleAddLot = async () => {
    const nb = Number(lotSeances);
    const tarif = Number(lotTarif);
    if (!nb || nb <= 0 || !tarif || tarif <= 0 || !user) return;
    const montant = nb * tarif;
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const date = lastDay.toISOString().slice(0, 10);
    const description = `${nb} séances × ${tarif}€ — ${MOIS_FULL[currentMonth]} ${currentYear}`;
    const { data, error } = await supabase.from("recettes").insert({ date, montant, description, user_id: user.id }).select().single();
    if (error) { toast({ title: "❌ Erreur", description: error.message, variant: "destructive" }); return; }
    setRecettes(prev => [...prev, { id: data.id, date: data.date, montant: data.montant, description: data.description }]);
    await saveSeances(nb, Number(dureeMoyenne));
    setSeancesMois(String(nb));
    toast({ title: `✅ ${nb} séances enregistrées`, description: `${fmt(montant)} ajoutés à ${MOIS_FULL[currentMonth]}.`, duration: 3000 });
  };


  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("recettes").delete().eq("id", id);
    if (error) {
      toast({ title: "❌ Erreur de suppression", description: "Vérifiez votre connexion.", variant: "destructive" });
      return;
    }
    setRecettes(prev => prev.filter(r => r.id !== id));
    setShowDeleteConfirm(null);
    setSaveStatus("✓ Supprimé");
    
    setTimeout(() => setSaveStatus(""), 2000);
  };

  const recettesFiltrees = useMemo(() => {
    let base = [...recettes].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
    if (filterMois !== "all") base = base.filter(r => {
      const d = new Date(r.date);
      return d.getFullYear() === currentYear && d.getMonth() === parseInt(filterMois);
    });
    return base;
  }, [recettes, filterMois]);

  // ─── EXPORT PDF ───────────────────────────────────────────────────────────
  const encodeHTML = (str: string): string =>
    str.replace(/&/g, "&amp;")
       .replace(/</g, "&lt;")
       .replace(/>/g, "&gt;")
       .replace(/"/g, "&quot;")
       .replace(/'/g, "&#039;");

  const exportPDF = () => {
    const watermarkCSS = isEssentiel ? `
      @media print {
        body::before {
          content: 'GÉNÉRÉ AVEC PSYGESTION GRATUIT — PASSEZ PRO POUR RETIRER CE FILIGRANE';
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-35deg);
          font-size: 38px;
          font-weight: bold;
          color: rgba(27, 58, 92, 0.08);
          white-space: nowrap;
          pointer-events: none;
          z-index: 9999;
        }
      }
    ` : "";
    const content = `
      <html><head><meta charset="UTF-8">
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; color: #1B3A5C; margin: 40px; }
        h1 { color: #1B3A5C; font-size: 20px; border-bottom: 3px solid #2A9D8F; padding-bottom: 8px; }
        h2 { color: #2E6DA4; font-size: 14px; margin-top: 24px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th { background: #1B3A5C; color: white; padding: 6px 10px; text-align: left; font-size: 11px; }
        td { padding: 5px 10px; border-bottom: 1px solid #D6E4F0; }
        .total { font-weight: bold; background: #EAF2FB; }
        .stat { display: inline-block; margin: 8px 16px 8px 0; }
        .stat-label { font-size: 10px; color: #6B85A3; text-transform: uppercase; }
        .stat-value { font-size: 18px; font-weight: bold; color: #1B3A5C; }
        .conseil { background: #E8F5F4; border-left: 4px solid #2A9D8F; padding: 10px; margin: 12px 0; }
        footer { margin-top: 40px; font-size: 9px; color: #6B85A3; text-align: center; border-top: 1px solid #D6E4F0; padding-top: 8px; }
        ${watermarkCSS}
      </style></head><body>
      <h1>🧠 PsyGestion — Bilan financier ${currentYear}</h1>
      <p style="color:#6B85A3;font-size:11px">Généré le ${today.toLocaleDateString("fr-FR")} · Régime ${isMicroBNC ? "Micro-BNC" : "BNC Classique"}</p>
      <h2>Indicateurs clés</h2>
      <div>
        <div class="stat"><div class="stat-label">Encaissé cette année</div><div class="stat-value">${fmt(stats.ca)}</div></div>
        <div class="stat"><div class="stat-label">URSSAF à payer</div><div class="stat-value">${fmt(stats.urssaf)}</div></div>
        <div class="stat"><div class="stat-label">Ce que vous touchez vraiment</div><div class="stat-value">${fmt(stats.revenuNet)}</div></div>
        <div class="stat"><div class="stat-label">Projection fin d'année</div><div class="stat-value">${fmt(stats.projectionFin)}</div></div>
      </div>
      <div class="conseil">${conseil.icon} <strong>${conseil.titre}</strong><br>${conseil.texte}</div>
      <h2>Récapitulatif mensuel</h2>
      <table>
        <tr><th>Mois</th><th>Encaissé</th><th>${isMicroBNC ? "Cotisations CIPAV (23,4% CA)" : "Cotisations estimées (35–45% bénéfice)"}</th><th>Ce que vous touchez</th><th>${isMicroBNC ? "Déduction légale (34%)" : "Charges réelles"}</th></tr>
        ${stats.parMois.map((m, i) => {
          const lastCol = isMicroBNC ? (m.ca > 0 ? fmt(m.ca * ABATTEMENT) : "—") : (m.ca > 0 ? fmt(stats.chargesDeduites / 12) : "—");
          return `<tr><td>${MOIS_FULL[i]}</td><td>${m.ca > 0 ? fmt(m.ca) : "—"}</td><td>${m.ca > 0 ? fmt(m.urssaf) : "—"}</td><td>${m.ca > 0 ? fmt(m.net) : "—"}</td><td>${lastCol}</td></tr>`;
        }).join("")}
        <tr class="total"><td>TOTAL</td><td>${fmt(stats.ca)}</td><td>${fmt(stats.urssaf)}</td><td>${fmt(stats.revenuNet)}</td><td>${isMicroBNC ? fmt(stats.ca * ABATTEMENT) : fmt(stats.chargesDeduites)}</td></tr>
      </table>
      <h2>Déclarations URSSAF trimestrielles</h2>
      <table>
        <tr><th>Trimestre</th><th>CA</th><th>URSSAF à déclarer</th><th>Échéance</th></tr>
        ${stats.parTrimestre.map(t => `<tr><td>${t.label}</td><td>${fmt(t.ca)}</td><td>${fmt(t.urssaf)}</td><td>${t.deadline}</td></tr>`).join("")}
      </table>
      <h2>Détail des recettes ${currentYear}</h2>
      <table>
        <tr><th>Date</th><th>Description</th><th>Montant</th><th>URSSAF</th><th>Net</th></tr>
        ${stats.recettesAnnee.sort((a: any,b: any) => new Date(a.date).getTime()-new Date(b.date).getTime()).map((r: any) => {
          const u = isMicroBNC ? Number(r.montant)*TAUX_URSSAF : 0;
          const n = isMicroBNC ? Number(r.montant) - u : Number(r.montant);
          return `<tr><td>${new Date(r.date).toLocaleDateString("fr-FR")}</td><td>${encodeHTML(String(r.description ?? ""))}</td><td>${fmt(r.montant)}</td><td>${isMicroBNC ? fmt(u) : "Voir total annuel"}</td><td>${isMicroBNC ? fmt(n) : "—"}</td></tr>`;
        }).join("")}
      </table>
      <footer>PsyGestion · ${isMicroBNC ? DISCLAIMER_MICRO : DISCLAIMER_BNC}</footer>
      </body></html>
    `;
    const win = window.open("", "_blank");
    win.document.write(content);
    win.document.close();
    win.print();
  };

  const barColor = stats.pctSeuil < 60 ? C.teal : stats.pctSeuil < 90 ? C.warning : C.danger;
  const objectifColor = stats.pctObjectif < 50 ? C.blue : stats.pctObjectif < 90 ? C.teal : C.success;


  if (!loaded) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: C.bg }}>
      <div className="text-center">
        <PsyGestionSpinner size={56} />
        <div className="text-sm mt-4" style={{ color: C.muted }}>Chargement de vos données...</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.bg, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Bannière founding member — masquée pour Pro actif */}
      {!isProActive && <FoundingBanner />}

      {/* Bannière rattrapage express — masquée pour Pro actif */}
      {!isProActive && (
        <div className="max-w-7xl mx-auto px-4">
          <QuickCatchupBanner caTotal={stats.ca} onRecettesSaved={() => window.location.reload()} />
        </div>
      )}

      {/* ── HEADER ── */}
      <header className="sticky z-50 shadow-sm" style={{ top: isTrial ? 44 : 0, backgroundColor: C.navy }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="shrink-0">
              <PsyGestionIcon size={36} />
            </div>
            <div className="min-w-0">
              <div className="text-white font-bold text-sm sm:text-base leading-tight flex items-center gap-1 sm:gap-2 flex-wrap">
                <span className="shrink-0">PsyGestion</span>
                {!hasRealData && (
                  <span className="hidden sm:inline text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: "rgba(255,255,255,0.15)", color: "#8BAAC8" }}>
                    {isMicroBNC ? "Micro-BNC" : "BNC Classique"}
                  </span>
                )}
                {isModePrevisionnel && !hasRealData && (
                  <span className="group relative text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full font-bold cursor-help" style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>
                    PRÉVISIONNEL ⓘ
                    <span className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 p-3 rounded-xl shadow-lg text-xs font-normal text-left z-50" style={{ backgroundColor: "#1B3A5C", color: "#fff" }}>
                      Estimations basées sur votre profil. Saisissez vos recettes pour la précision réelle.
                    </span>
                  </span>
                )}
                {hasRealData && isProActive && (
                  <span className="text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: "#D1FAE5", color: "#065F46" }}>
                    ✓ RÉEL
                  </span>
                )}
              </div>
              <div className="text-[10px] sm:text-xs hidden sm:block" style={{ color: "#8BAAC8" }}>
                Tableau de bord {hasRealData ? "" : (isMicroBNC ? "Micro-BNC " : "BNC Classique ")}— {currentYear}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {saveStatus && <span className="text-[10px] sm:text-xs px-2 py-1 rounded-full hidden sm:inline" style={{ backgroundColor: C.teal, color: "white" }}>{saveStatus}</span>}
            {stats.ca > SEUIL_ALERTE && <span className="text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full hidden sm:inline" style={{ backgroundColor: C.danger, color: "#fff" }}>⚠ Seuil</span>}
            <AvatarDropdown email={user?.email || ""} onOpenFiscalModal={() => setActiveTab("profil")} onExportPDF={exportPDF} showChevron />
          </div>
        </div>
        {/* ── NAVIGATION ADAPTATIVE ── */}
        {(() => {
          const ONGLETS_ESSENTIEL = [
            { id: "dashboard", label: "📊 Tableau de bord" },
            { id: "saisie",    label: "➕ Saisir" },
            { id: "assistant", label: "🧠 Assistant" },
          ];
          const ONGLETS_PLUS_ESSENTIEL = [
            { id: "projection",  label: "📈 Projection" },
            { id: "simulateur",  label: "🔄 Simulateur" },
            { id: "urssaf",      label: "🏛️ URSSAF" },
            ...(!isMicroBNC ? [{ id: "charges2035", label: "📋 Charges 2035" }] : []),
            { id: "historique",  label: "📁 Historique" },
            { id: "objectif",    label: "🎯 Objectif" },
            { id: "reforme",     label: "🗓️ Réforme 2026" },
          ];
          const ONGLETS_COMPLET = [
            { id: "dashboard",  label: "📊 Tableau de bord" },
            { id: "saisie",     label: "➕ Saisir" },
            { id: "projection", label: "📈 Projection" },
            { id: "simulateur", label: "🔄 Simulateur" },
            { id: "assistant",  label: "🧠 Assistant" },
          ];
          const ONGLETS_PLUS_COMPLET = [
            { id: "urssaf",     label: "🏛️ URSSAF" },
            ...(!isMicroBNC ? [{ id: "charges2035", label: "📋 Charges 2035" }] : []),
            { id: "historique", label: "📁 Historique" },
            { id: "objectif",   label: "🎯 Objectif" },
            { id: "reforme",    label: "🗓️ Réforme 2026" },
          ];

          const visibleOnglets = mode === "debutante" ? ONGLETS_ESSENTIEL : ONGLETS_COMPLET;
          const plusOnglets    = mode === "debutante" ? ONGLETS_PLUS_ESSENTIEL : ONGLETS_PLUS_COMPLET;
          const activeInPlus   = plusOnglets.some(o => o.id === activeTab);

          return (
            <div className="max-w-7xl mx-auto px-2 sm:px-4 flex items-center">
              {/* Scrollable tabs — isolated overflow so the Plus dropdown is never clipped */}
              <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide flex-1 min-w-0" style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
                {visibleOnglets.map(({ id, label }) => (
                  <button key={id} onClick={() => setActiveTab(id)}
                    className="px-2 sm:px-3 py-2 sm:py-2.5 text-[11px] sm:text-xs font-semibold rounded-t-lg transition-colors whitespace-nowrap shrink-0"
                    style={{ backgroundColor: activeTab === id ? C.bg : "transparent", color: activeTab === id ? C.navy : "#8BAAC8", minHeight: 44 }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* ··· Plus — outside the overflow container so the dropdown renders unclipped */}
              <div className="relative shrink-0" ref={plusMenuRef}>
                <button
                  onClick={() => setShowPlusMenu(v => !v)}
                  className="px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap flex items-center gap-1"
                  style={{
                    backgroundColor: showPlusMenu || activeInPlus ? C.bg : "transparent",
                    color: showPlusMenu || activeInPlus ? C.navy : "#8BAAC8",
                    borderRadius: "8px 8px 0 0",
                    minHeight: 44,
                  }}
                >
                  ··· Plus
                  <ChevronDown size={14} className="transition-transform" style={{ transform: showPlusMenu ? "rotate(180deg)" : "rotate(0)" }} />
                  {activeInPlus && !showPlusMenu && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: C.teal }}>
                      {plusOnglets.length}
                    </span>
                  )}
                </button>

                {showPlusMenu && (
                  <>
                    {/* Overlay mobile */}
                    <div className="fixed inset-0 z-[150] sm:hidden bg-black/20" onClick={() => setShowPlusMenu(false)} />
                    {/* Dropdown (desktop) / Bottom sheet (mobile) */}
                    <div
                      className="fixed bottom-0 left-0 right-0 z-[200] rounded-t-xl sm:absolute sm:bottom-auto sm:left-auto sm:right-0 sm:top-full sm:w-52 sm:rounded-xl"
                      style={{
                        backgroundColor: C.white,
                        border: `1px solid ${C.border}`,
                        boxShadow: "0 12px 40px rgba(27,58,92,0.22)",
                      }}
                    >
                      <div className="rounded-t-xl sm:rounded-xl overflow-hidden py-1">
                        {plusOnglets.map(({ id, label }) => (
                          <button key={id}
                            onClick={() => { setActiveTab(id); setShowPlusMenu(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50 flex items-center gap-2.5"
                            style={{ color: activeTab === id ? C.teal : C.navy }}
                          >
                            <span className="text-base leading-none">{label.split(" ")[0]}</span>
                            <span>{label.split(" ").slice(1).join(" ")}</span>
                          </button>
                        ))}

                        <div className="mx-3 my-1" style={{ borderTop: `1px solid ${C.border}` }} />

                        <button
                          onClick={() => { setActiveTab("profil"); setShowPlusMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50 flex items-center gap-2.5"
                          style={{ color: C.navy }}
                        >
                          <Settings size={16} style={{ color: C.muted }} />
                          Paramètres Fiscaux
                        </button>
                        <button
                          onClick={() => { exportPDF(); setShowPlusMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50 flex items-center gap-2.5"
                          style={{ color: C.navy }}
                        >
                          <FileText size={16} style={{ color: C.muted }} />
                          Export PDF
                        </button>

                        <div className="mx-3 my-1" style={{ borderTop: `1px solid ${C.border}` }} />

                        <button
                          onClick={() => { signOut(); setShowPlusMenu(false); }}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium transition-colors hover:bg-red-50 flex items-center gap-2.5"
                          style={{ color: C.danger }}
                        >
                          <LogOut size={16} />
                          Déconnexion
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </header>

      <main className="max-w-7xl mx-auto px-4 pb-6">

        {/* ══════════════════ DASHBOARD ══════════════════ */}
        {activeTab === "dashboard" && (
          <div className="space-y-5">

            {/* ── INSIGHT FLASH — post-import ── */}
            {importInsight && (() => {
              // Seuil d'alerte = 72% du plafond Micro-BNC (signal précoce)
              const SEUIL_ALERTE_PRECOCE = Math.round(SEUIL_MICRO_BNC * 0.72); // ~60 192€
              const ratio = importInsight.totalRecettes > 0
                ? importInsight.totalCharges / importInsight.totalRecettes
                : 0;
              const approcheSeuil = importInsight.totalRecettes > SEUIL_ALERTE_PRECOCE;
              const chargesTooHigh = ratio > ABATTEMENT_MICRO_BNC;
              if (!chargesTooHigh && !approcheSeuil) return null;
              const fmtEur = (n: number) =>
                new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
              // Économie BNC réel estimée : excédent de charges × taux marginal 30%
              const economieBNCReel = Math.max(0, Math.round(
                (importInsight.totalCharges - importInsight.totalRecettes * ABATTEMENT_MICRO_BNC) * 0.30
              ));
              return (
                <div className="space-y-2">
                  {chargesTooHigh && (
                    <div className="rounded-2xl px-4 py-3 flex items-start gap-3" style={{ backgroundColor: "#FEE8EA", border: "1px solid #E6394640" }}>
                      <span className="text-lg shrink-0">🚨</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold" style={{ color: "#C0392B" }}>
                          Vos charges dépassent l'abattement forfaitaire ({Math.round(ABATTEMENT_MICRO_BNC * 100)}%)
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: "#C0392B" }}>
                          Vous avez {Math.round(ratio * 100)}% de charges — le BNC réel pourrait vous faire économiser{" "}
                          <strong>{fmtEur(economieBNCReel)}</strong>{" "}d'impôts.
                        </p>
                      </div>
                      <button onClick={() => setImportInsight(null)} className="shrink-0 opacity-50 hover:opacity-100" style={{ color: "#C0392B" }}>
                        <span style={{ fontSize: 16 }}>✕</span>
                      </button>
                    </div>
                  )}
                  {approcheSeuil && (
                    <div className="rounded-2xl px-4 py-3 flex items-start gap-3" style={{ backgroundColor: "#FFF3E0", border: "1px solid #F4A26140" }}>
                      <span className="text-lg shrink-0">⚠️</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold" style={{ color: "#E67E22" }}>
                          Vigilance seuil Micro-BNC {fmtEur(SEUIL_MICRO_BNC)}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: "#E67E22" }}>
                          Votre CA importé ({fmtEur(importInsight.totalRecettes)}) approche le plafond.
                          Au-delà, passage obligatoire au BNC réel.
                        </p>
                      </div>
                      <button onClick={() => setImportInsight(null)} className="shrink-0 opacity-50 hover:opacity-100" style={{ color: "#E67E22" }}>
                        <span style={{ fontSize: 16 }}>✕</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── TIP CA — carte d'onboarding (aucune recette réelle + non-Pro actif) ── */}
            {!hasRealData && !isProActive && (
              <div
                className="rounded-2xl p-5 backdrop-blur-sm"
                style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.teal} 100%)` }}
              >
                <div className="font-bold text-white text-base mb-1">
                  📊 Débloquez votre tableau de bord
                </div>
                <div className="text-sm mb-3" style={{ color: "rgba(255,255,255,0.8)" }}>
                  Saisissez votre première recette pour voir vos vrais chiffres — CA, cotisations CIPAV, revenu net.
                </div>
                <button
                  onClick={() => setActiveTab("saisie")}
                  className="font-semibold text-sm px-4 py-2 rounded-xl transition-all hover:brightness-110 active:scale-[0.97]"
                  style={{ backgroundColor: "rgba(255,255,255,0.18)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }}
                >
                  Saisir ma première recette →
                </button>
              </div>
            )}

            {/* ── ZONE HERO — 3 chiffres principaux ── */}
            {(() => {
              const caMoisCourant = stats.parMois[currentMonth].ca;
              const provisionMois = stats.provisionMensuelle;
              const provisionEstOrange = caMoisCourant > 0 && provisionMois > caMoisCourant * 0.4;
              const provisionColor = provisionEstOrange ? C.warning : C.teal;

              const dureeH = Number(dureeMoyenne) === 45 ? 0.75 : 1;

              // Taux horaire avec fallback onboarding
              let tauxHoraireNet: number | null = null;
              let tauxHoraireLabel = "Votre taux horaire";
              let tauxHoraireSub = "Saisissez vos séances dans l'onglet Saisir";
              
              if (seancesAnnuelles > 0) {
                tauxHoraireNet = Math.round(stats.revenuNet / (seancesAnnuelles * dureeH));
                tauxHoraireLabel = `Vous gagnez ${tauxHoraireNet} €/heure`;
                tauxHoraireSub = "net après CIPAV et IR barème 2026";
              } else if (onboardingData.avgRate > 0 && onboardingData.targetCa > 0) {
                // Estimation from onboarding: (CA - charges - URSSAF) / nb séances
                const caAn = onboardingData.targetCa * 12;
                const chargesAn = onboardingData.estCharges * 12;
                const urssafEst = Math.round(caAn * TAUX_URSSAF);
                const netEst = Math.max(0, caAn - chargesAn - urssafEst);
                const nbSeancesEst = Math.round(caAn / onboardingData.avgRate);
                tauxHoraireNet = nbSeancesEst > 0 ? Math.round(netEst / (nbSeancesEst * dureeH)) : null;
                if (tauxHoraireNet !== null) {
                  tauxHoraireLabel = `Estimé : ${tauxHoraireNet} €/heure`;
                  tauxHoraireSub = "basé sur votre profil d'onboarding";
                }
              } else if (stats.isEstimation && profile?.tarif_seance && profile?.nb_patients_semaine) {
                tauxHoraireNet = Math.round(stats.revenuNet / (profile.nb_patients_semaine * (profile.semaines_travaillees ?? 45) * dureeH));
                tauxHoraireLabel = `Estimé : ${tauxHoraireNet} €/heure`;
                tauxHoraireSub = "basé sur votre profil";
              }

              // Point mort depuis onboarding
              const pointMortSeances = onboardingData.avgRate > 0 && onboardingData.estCharges > 0
                ? Math.ceil(onboardingData.estCharges / onboardingData.avgRate)
                : null;

              // URSSAF prévisionnel
              const urssafPrev = onboardingData.targetCa > 0
                ? Math.round(onboardingData.targetCa * TAUX_URSSAF)
                : null;

              const isPrevisionnel = stats.isEstimation || isModePrevisionnel;

              // Dynamic labels based on data state
              const card1Label = stats.isEstimation
                ? "CA annuel estimé"
                : hasRealData && !hasFullYear
                  ? "CA annuel réel + projeté"
                  : hasFullYear
                    ? "CA annuel réel"
                    : "Encaissé ce mois";

              const card2Label = stats.isEstimation && urssafPrev
                ? "URSSAF à provisionner/mois"
                : hasRealData
                  ? "URSSAF/CIPAV réel (Provision)"
                  : "À mettre de côté";

              const EstimationBadge = () => (
                !hasRealData ? (
                  <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ml-1" style={{ backgroundColor: "#FFF3E0", color: "#E65100", border: "1px solid #FFB74D" }}>
                    {isPrevisionnel ? "Prévisionnel" : "Estimation"}
                  </span>
                ) : null
              );

              return (
                <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ alignItems: "stretch" }}>
                  {/* Card 1 — CA */}
                  <Card className="flex flex-col gap-1">
                    <div className="text-xs font-semibold uppercase tracking-widest mb-1 flex items-center" style={{ color: C.muted }}>
                      {card1Label}
                      {stats.isEstimation && <EstimationBadge />}
                    </div>
                    <div className="font-bold" style={{ color: C.teal, fontSize: 40, lineHeight: 1.1 }}>
                      {stats.isEstimation ? fmt(stats.ca) : hasRealData && !hasFullYear ? fmt(stats.projectionFin) : fmt(caMoisCourant)}
                    </div>
                    <div className="text-xs mt-1" style={{ color: C.muted }}>
                      {stats.isEstimation
                        ? onboardingData.targetCa > 0
                          ? `Objectif : ${fmt(onboardingData.targetCa)}/mois`
                          : `${profile?.tarif_seance}€ × ${profile?.nb_patients_semaine} patients × ${profile?.semaines_travaillees ?? 45} semaines`
                        : hasRealData && !hasFullYear
                          ? `${fmt(caReelTotal)} réel + ${fmt(Math.max(0, stats.projectionFin - caReelTotal))} projeté (${moisAvecRecettesCount}/12 mois saisis)`
                          : stats.moyenneMensuelle > 0
                            ? `sur ${fmt(stats.moyenneMensuelle)} en moyenne par mois`
                            : `${MOIS_FULL[currentMonth]} ${currentYear}`}
                    </div>
                    {/* CTA — Saisir mes recettes réelles */}
                    {stats.isEstimation && (
                      <button
                        onClick={() => setActiveTab("saisie")}
                        className="mt-3 w-full py-2.5 rounded-xl text-white font-bold text-sm transition-opacity hover:opacity-90 active:scale-[0.97]"
                        style={{ backgroundColor: C.teal }}
                      >
                        Saisir mes recettes réelles de {currentYear} →
                      </button>
                    )}
                  </Card>

                  {/* Card 2 — À mettre de côté (Social + Fiscal décomposés) */}
                  <Card className="flex flex-col gap-1">
                    <div className="text-xs font-semibold uppercase tracking-widest mb-1 flex items-center" style={{ color: C.muted }}>
                      {card2Label}
                      {(stats.isEstimation || isModePrevisionnel) && <EstimationBadge />}
                    </div>
                    {stats.isEstimation && urssafPrev ? (
                      <>
                        <div className="font-bold" style={{ color: C.purple, fontSize: 40, lineHeight: 1.1 }}>{fmt(urssafPrev)}</div>
                        <div className="text-xs mt-1" style={{ color: C.muted }}>CIPAV 23,4% sur {fmt(onboardingData.targetCa)}/mois</div>
                      </>
                    ) : (
                      (() => {
                        const caRef = stats.isEstimation ? stats.ca : stats.parMois[currentMonth].ca;
                        const socialMois = isMicroBNC
                          ? Math.round(calculProvisionSociale(caRef) / (stats.isEstimation ? 12 : 1))
                          : Math.round(Math.max(0, caRef - stats.chargesDeduites / 12) * TAUX_URSSAF_BNC);
                        const fiscalMois = isMicroBNC
                          ? Math.round(calculProvisionFiscale(stats.isEstimation ? stats.ca : caRef * 12) / 12)
                          : 0;
                        const totalMois = socialMois + fiscalMois;
                        const provisionColor = totalMois > caRef * 0.4 ? C.warning : C.teal;
                        return (
                          <>
                            <div className="font-bold" style={{ color: provisionColor, fontSize: 40, lineHeight: 1.1 }}>{fmt(totalMois)}</div>
                            <div className="text-xs mt-1 space-y-0.5">
                              <div style={{ color: C.blue }}>Social (CIPAV 23,4%) : {fmt(socialMois)}</div>
                              {isMicroBNC && <div style={{ color: C.warning }}>Fiscal (IR barème) : {fmt(fiscalMois)}</div>}
                            </div>
                          </>
                        );
                      })()
                    )}
                    {/* Badge J−X Réforme 2026 — deadline changement de régime */}
                    {(() => {
                      const deadline = new Date("2026-12-31");
                      const jours = Math.ceil((deadline.getTime() - new Date().getTime()) / 86_400_000);
                      if (jours <= 0) return null;
                      const color = jours < 60 ? C.danger : jours < 120 ? C.warning : C.teal;
                      return (
                        <button
                          onClick={() => setActiveTab("reforme")}
                          className="mt-3 w-full flex items-center justify-between px-3 py-2 rounded-xl text-[11px] font-semibold transition-opacity hover:opacity-80"
                          style={{ backgroundColor: color + "15", border: `1px solid ${color}40`, color }}
                        >
                          <span>🗓️ Réforme 2026 — changer de régime avant le 31 déc.</span>
                          <span className="font-extrabold shrink-0">J−{jours}</span>
                        </button>
                      );
                    })()}
                  </Card>

                  {/* Card 3 — Taux horaire + Temps Libre */}
                  <Card className="flex flex-col gap-1">
                    <div className="text-xs font-semibold uppercase tracking-widest mb-1 flex items-center" style={{ color: C.muted }}>
                      {tauxHoraireLabel}
                      {isPrevisionnel && tauxHoraireNet !== null && <EstimationBadge />}
                    </div>
                    {tauxHoraireNet !== null ? (
                      <>
                        <div className="font-bold" style={{ color: C.navy, fontSize: 40, lineHeight: 1.1 }}>{tauxHoraireNet} €<span className="text-lg">/h</span></div>
                        <div className="text-xs mt-1" style={{ color: C.muted }}>{tauxHoraireSub}</div>
                        {stats.ir > 0 && (
                          <div className="mt-1.5 text-[10px] px-2 py-1 rounded-lg inline-block" style={{ backgroundColor: C.bg, color: C.muted }}>
                            ℹ️ IR inclus : ≈ {fmt(stats.ir)}/an · {fmt(Math.round(stats.ir / 12))}/mois
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="font-bold" style={{ color: C.muted, fontSize: 40, lineHeight: 1.1 }}>— €/h</div>
                        <div className="text-xs mt-1" style={{ color: C.muted }}>{tauxHoraireSub}</div>
                      </>
                    )}

                  </Card>
                </div>

                {/* Point mort card — visible en mode prévisionnel */}
                {isPrevisionnel && pointMortSeances !== null && (
                  <Card>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🎯</span>
                      <div>
                        <div className="font-bold text-sm" style={{ color: C.navy }}>
                          {pointMortSeances} séances/mois pour couvrir vos charges
                        </div>
                        <div className="text-xs" style={{ color: C.muted }}>
                          Charges estimées : {fmt(onboardingData.estCharges)}/mois ÷ {fmt(onboardingData.avgRate)}/séance
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Alerte seuil Micro-BNC — passage obligatoire */}
                {isMicroBNC && stats.projectionFin > SEUIL_CA && (
                  <Card style={{ backgroundColor: "#FFF0F1", borderColor: C.danger, borderWidth: 2 }}>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">🚨</span>
                      <div>
                        <div className="font-bold text-sm" style={{ color: C.danger }}>
                          Passage obligatoire au BNC Classique en 2027
                        </div>
                        <div className="text-xs mt-1" style={{ color: C.muted }}>
                          Votre CA projeté ({fmt(stats.projectionFin)}) dépasse le seuil Micro-BNC de {fmt(SEUIL_CA)}. En cas de dépassement 2 années consécutives, le passage au régime réel est obligatoire.
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* ── CHECKLIST TRANSITION MICRO → RÉEL ── */}
                {isMicroBNC && (() => {
                  const chargesAnnRef = stats.chargesDeduites > 0 ? stats.chargesDeduites : onboardingData.estCharges * 12;
                  const tauxChargesRef = stats.ca > 0 ? chargesAnnRef / stats.ca : chargesAnnRef / Math.max(1, caEstime);
                  const showChecklist = tauxChargesRef >= 0.30 || stats.projectionFin > 55000;
                  if (!showChecklist) return null;

                  const steps = [
                    {
                      done: true,
                      icon: "✅",
                      title: "Valider que le Réel est rentable pour vous",
                      desc: "PsyGestion l'a calculé — votre taux de charges dépasse 30% de votre CA.",
                    },
                    {
                      done: false,
                      icon: "👩‍💼",
                      title: "Contacter un expert-comptable",
                      desc: "Coût habituel : 800–1 500€/an. Il gérera votre déclaration 2035 et validera votre situation.",
                    },
                    {
                      done: false,
                      icon: "🏦",
                      title: "Ouvrir un compte bancaire dédié",
                      desc: "Obligatoire en BNC Classique. Séparez recettes pro et dépenses personnelles dès maintenant.",
                    },
                    {
                      done: false,
                      icon: "📅",
                      title: "Déclarer votre changement de régime",
                      desc: "Avant le 31 décembre pour une application au 1er janvier. À faire sur impots.gouv.fr.",
                    },
                    {
                      done: false,
                      icon: "⚙️",
                      title: "Basculer PsyGestion en BNC Classique",
                      desc: "Activez le mode BNC dans votre profil — vos charges seront déductibles et votre 2035 se prépare automatiquement.",
                      cta: true,
                    },
                  ];

                  return (
                    <Card style={{ borderColor: C.teal, borderWidth: 2 }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">🗺️</span>
                        <span className="font-bold text-sm" style={{ color: C.navy }}>Votre chemin vers le Réel — 5 étapes</span>
                        <span className="ml-auto text-xs px-2.5 py-1 rounded-full font-semibold" style={{ backgroundColor: C.tealBg, color: C.teal }}>
                          1/5 complétée
                        </span>
                      </div>
                      <p className="text-xs mb-4" style={{ color: C.muted }}>
                        Votre taux de charges ({Math.round(tauxChargesRef * 100)}%) dépasse le seuil de rentabilité du Réel. Voici comment passer sans stress.
                      </p>
                      <div className="space-y-3">
                        {steps.map((s, i) => (
                          <div
                            key={i}
                            className="flex items-start gap-3 rounded-xl px-3 py-2.5"
                            style={{
                              backgroundColor: s.done ? C.tealBg : "#F7FAFE",
                              border: `1px solid ${s.done ? C.teal + "60" : C.border}`,
                            }}
                          >
                            <span className="text-base shrink-0 mt-0.5">{s.icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm" style={{ color: s.done ? C.teal : C.navy }}>
                                {i + 1}. {s.title}
                              </div>
                              <div className="text-xs mt-0.5" style={{ color: C.muted }}>{s.desc}</div>
                              {s.cta && (
                                <button
                                  onClick={() => setActiveTab("profil")}
                                  className="mt-2 text-xs font-bold px-3 py-1.5 rounded-lg text-white"
                                  style={{ backgroundColor: C.teal }}
                                >
                                  Configurer en BNC Classique →
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })()}

                {/* Statut TVA */}
                {isMicroBNC && (() => {
                  const hasTVA = hasRecettesSoumisesTVA(stats.recettesAnnee);
                  const caTVA = hasTVA ? calculCASoumiseTVA(stats.recettesAnnee) : 0;
                  return (
                    <Card>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{hasTVA ? "📋" : "✅"}</span>
                        <div className="flex-1">
                          <div className="font-bold text-sm" style={{ color: hasTVA ? C.warning : C.teal }}>
                            TVA : {hasTVA ? `Suivi activé — ${fmt(caTVA)} soumis / ${fmt(SEUIL_TVA)} seuil` : "Exonéré (Soins)"}
                          </div>
                          <div className="text-xs mt-0.5" style={{ color: C.muted }}>
                            {hasTVA
                              ? `Vos recettes de formation/expertise sont soumises au seuil de ${fmt(SEUIL_TVA)}. Soins psychologiques exonérés (art. 261-4-1° CGI).`
                              : "Les prestations de soins psychologiques sont exonérées de TVA sans limite de seuil pour les titulaires des diplômes requis."}
                          </div>
                        </div>
                        {hasTVA && caTVA > SEUIL_TVA && (
                          <span className="text-xs px-2.5 py-1 rounded-full font-bold" style={{ backgroundColor: "#FFF0F1", color: C.danger }}>⚠️ Dépassé</span>
                        )}
                      </div>
                    </Card>
                  );
                })()}

                </div>
              );
            })()}

            {/* ── FEU TRICOLORE + ACTION ── */}
            {(() => {
              const caMoisCourant = stats.parMois[currentMonth].ca;
              // Pour le feu tricolore, on utilise TAUX_URSSAF (Micro) ou une provision indicative (BNC)
              const provisionURSSAF = isMicroBNC ? caMoisCourant * TAUX_URSSAF : caMoisCourant * TAUX_URSSAF_BNC * 0.5; // BNC: provision indicative sur CA brut
              const provisionIR = isMicroBNC ? caMoisCourant * PROVISION_IR : 0;
              const aMettreDecote = provisionURSSAF + provisionIR;

              const urssafNonProvisionnee = stats.urssaf > 0 && stats.parTrimestre[currentQuarter]?.urssaf > 0;

              let feu: "red" | "yellow" | "green";
              let feuEmoji: string;
              let feuLabel: string;
              if (isMicroBNC) {
                if (stats.ca > SEUIL_ALERTE || stats.projectionFin > SEUIL_CA) {
                  feu = "red"; feuEmoji = "🔴"; feuLabel = "Attention urgente — Seuil critique Micro-BNC";
                } else if (stats.ca > SEUIL_TVA || urssafNonProvisionnee) {
                  feu = "yellow"; feuEmoji = "🟡"; feuLabel = "Vigilance — Pensez à provisionner";
                } else {
                  feu = "green"; feuEmoji = "🟢"; feuLabel = "Tout va bien";
                }
              } else {
                // BNC classique
                if (stats.ca > 250000) {
                  feu = "red"; feuEmoji = "🔴"; feuLabel = "Attention — Seuil BNC classique approché";
                } else if (!Number(chargesReelles)) {
                  feu = "yellow"; feuEmoji = "🟡"; feuLabel = "Pensez à saisir vos charges déductibles";
                } else {
                  feu = "green"; feuEmoji = "🟢"; feuLabel = "Tout va bien";
                }
              }

              const feuColors = {
                red: { bg: "#FFF0F1", border: C.danger, text: C.danger },
                yellow: { bg: "#FFF8F0", border: C.warning, text: "#C46200" },
                green: { bg: C.lightTeal, border: C.teal, text: C.teal },
              }[feu];

              return (
                <div className="rounded-2xl p-5 border-2 flex items-center gap-5" style={{ backgroundColor: feuColors.bg, borderColor: feuColors.border }}>
                  <span className="text-4xl">{feuEmoji}</span>
                  <div className="flex-1">
                    <div className="font-bold text-base mb-1" style={{ color: feuColors.text }}>
                      {feu === "yellow" ? "Vigilance — Pensez à provisionner" : feuLabel}
                    </div>
                    <div className="text-lg font-semibold" style={{ color: C.navy }}>
                      {caMoisCourant > 0
                        ? `Mets ${fmt(aMettreDecote)} de côté ce mois-ci`
                        : "Aucune recette ce mois-ci — rien à provisionner pour l'instant"}
                    </div>
                    {caMoisCourant > 0 && (
                      <div className="text-xs mt-1" style={{ color: C.muted }}>
                        {isMicroBNC ? `${fmt(provisionURSSAF)} cotisations CIPAV (23,4%) + ${fmt(provisionIR)} provision IR estimée (15%)` : `Provision indicative (estimation) sur ${fmt(caMoisCourant)} de CA en ${MOIS_FULL[currentMonth]}`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            <div className="rounded-2xl p-4 flex gap-3 items-start border" style={{ backgroundColor: cc.bg, borderColor: cc.border }}>
              <span className="text-2xl">{conseil.icon}</span>
              <div>
                <div className="font-bold text-sm mb-1" style={{ color: cc.text }}>{conseil.titre}</div>
                <div className="text-sm" style={{ color: C.navy }}>{conseil.texte}</div>
              </div>
            </div>

            {/* Simuler un changement CTA */}
            <button
              onClick={() => {
                const chargesAnnRef = chargesFromDB > 0 ? chargesFromDB : onboardingData.estCharges * 12;
                setSimulPreFill({ charges: { autres: chargesAnnRef }, quickMode: true });
                setActiveTab("simulateur");
              }}
              className="w-full py-3 rounded-2xl font-bold text-sm transition-all hover:opacity-90 active:scale-95 flex items-center justify-center gap-2"
              style={{ backgroundColor: C.lightTeal, color: C.teal, border: `2px solid ${C.teal}` }}
            >
              🔮 Simuler un changement →
            </button>

            {/* Seuil / Charges section */}
            <div className="grid md:grid-cols-2 gap-4">
              {isMicroBNC ? (
                <Card>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold" style={{ color: C.navy }}>Seuil Micro-BNC ({fmt(SEUIL_CA)})</span>
                    <span className="text-sm font-bold" style={{ color: barColor }}>{stats.pctSeuil.toFixed(1)}%</span>
                  </div>
                  <div className="w-full h-4 rounded-full overflow-hidden" style={{ backgroundColor: C.border }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${stats.pctSeuil}%`, backgroundColor: barColor }} />
                  </div>
                  <div className="flex justify-between mt-2 text-xs" style={{ color: C.muted }}>
                    <span>CA actuel : <strong style={{ color: C.navy }}>{fmt(stats.ca)}</strong></span>
                    <span>Restant : <strong style={{ color: C.navy }}>{fmt(stats.caRestant)}</strong></span>
                  </div>
                </Card>
              ) : (
                <Card>
                  <div className="text-sm font-bold mb-3" style={{ color: C.navy }}>💼 Vos charges déductibles cette année</div>
                  <div className="flex gap-2 items-end">
                    <input type="number" placeholder="Total charges réelles (€)" value={chargesReellesInput}
                      onChange={e => setChargesReellesInput(e.target.value)} min="0" step="100"
                      className="flex-1 rounded-xl border px-4 py-2 text-sm outline-none"
                      style={{ borderColor: C.border, color: C.navy }} />
                    <button onClick={() => { setChargesReelles(chargesReellesInput); }}
                      className="px-4 py-2 rounded-xl text-white text-sm font-semibold"
                      style={{ backgroundColor: C.teal }}>
                      OK
                    </button>
                  </div>
                  {Number(chargesReelles) > 0 && (
                    <div className="mt-2 text-xs" style={{ color: C.muted }}>
                      Charges réelles déduites : <strong style={{ color: C.navy }}>{fmt(Number(chargesReelles))}</strong> · Résultat net : <strong style={{ color: C.teal }}>{fmt(stats.revenuNet)}</strong>
                    </div>
                  )}
                </Card>
              )}
              <Card>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold" style={{ color: C.navy }}>Objectif personnel ({fmt(objectifCA)})</span>
                  <span className="text-sm font-bold" style={{ color: objectifColor }}>{stats.pctObjectif.toFixed(1)}%</span>
                </div>
                <div className="w-full h-4 rounded-full overflow-hidden" style={{ backgroundColor: C.border }}>
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${stats.pctObjectif}%`, backgroundColor: objectifColor }} />
                </div>
                <div className="flex justify-between mt-2 text-xs" style={{ color: C.muted }}>
                  <span>Réalisé : <strong style={{ color: C.navy }}>{fmt(stats.ca)}</strong></span>
                  <span>Restant : <strong style={{ color: C.navy }}>{fmt(Math.max(0, objectifCA - stats.ca))}</strong></span>
                </div>
              </Card>
            </div>

            {/* ── TOGGLE DÉTAILS ── */}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  const next = !showDetails;
                  setShowDetails(next);
                  localStorage.setItem("psygestion_details_open", String(next));
                }}
                className="text-sm px-4 py-2 rounded-full transition-colors hover:bg-gray-100"
                style={{ color: C.muted }}
              >
                {showDetails ? "Masquer le détail ▴" : "Voir le détail de mes finances ▾"}
              </button>
            </div>

            {/* ── ZONE DÉTAILS ── */}
            <div
              style={{
                overflow: "hidden",
                maxHeight: showDetails ? "9999px" : "0",
                opacity: showDetails ? 1 : 0,
                transition: "max-height 300ms ease, opacity 300ms ease",
              }}
            >
              <div className="space-y-5">

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <StatCard label="Encaissé cette année" value={fmt(stats.ca)} rawValue={stats.ca} icon="💶" color={C.navy} sub={String(currentYear)} />
              <StatCard label="Cotisations CIPAV" value={fmt(stats.urssaf)} rawValue={stats.urssaf} icon="🏛️" color={C.blue} sub={isMicroBNC ? "23,4% du CA (CIPAV + CFP)" : "Estimation BNC classique"} />
              <StatCard label="Ce que vous touchez vraiment" value={fmt(stats.revenuNet)} rawValue={stats.revenuNet} icon="✨" color={C.teal}
                sub={isMicroBNC
                  ? `Après CIPAV (23,4%) + IR ≈ ${fmt(stats.ir)}`
                  : `Après charges réelles + IR ≈ ${fmt(stats.ir)}`} />
              <StatCard label="En moyenne par mois" value={fmt(stats.moyenneMensuelle)} rawValue={stats.moyenneMensuelle} icon="📅" color={C.navy} />
              <StatCard label="À mettre de côté ce mois" value={fmt(stats.provisionMensuelle)} rawValue={stats.provisionMensuelle} icon="🏦" color={C.purple} highlight={isMicroBNC ? "Cotisations 23,2% + IR estimé" : "Charges estimées à provisionner"} />
              <StatCard label="Fin d'année estimée" value={fmt(stats.projectionFin)} rawValue={stats.projectionFin} icon="🔮" color={isMicroBNC && stats.projectionFin > SEUIL_CA ? C.danger : C.navy} pulse={isMicroBNC && stats.projectionFin > SEUIL_CA} sub={isMicroBNC ? `Seuil : ${fmt(SEUIL_CA)}` : undefined} />
            </div>

            {/* Disclaimer URSSAF BNC classique */}
            {!isMicroBNC && (
              <div className="rounded-xl px-4 py-3 text-xs border-l-4" style={{ backgroundColor: "#FFF8F0", borderLeftColor: C.warning, color: "#6B85A3" }}>
                ⚠️ Estimation indicative — vos cotisations réelles sont calculées par l'URSSAF sur votre bénéfice net déclaré. Consultez <a href="https://www.urssaf.fr" target="_blank" rel="noopener noreferrer" style={{ color: C.teal, textDecoration: "underline" }}>urssaf.fr</a> pour votre montant exact.
              </div>
            )}

            {/* Card Prélèvement à la source */}
            <PrelevementSourceCard ca={stats.ca} parMois={stats.parMois} chargesDeduites={stats.chargesDeduites} isMicroBNC={isMicroBNC} onNavigateCharges={() => setActiveTab("charges2035")} />

            {/* Card Point mort */}
            <PointMortCard
              ca={stats.ca}
              chargesDeduites={stats.chargesDeduites}
              isMicroBNC={isMicroBNC}
              tarifSeance={profile?.tarif_seance ?? null}
              onNavigateProfil={() => setActiveTab("profil")}
            />

            {isMicroBNC && stats.ca > 30000 && (
              <div className="rounded-2xl p-4 border cursor-pointer transition-colors hover:shadow-md"
                style={{ backgroundColor: "#F0F4FF", borderColor: C.blue }}
                onClick={() => setActiveTab("simulateur")}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔄</span>
                  <div>
                    <div className="font-bold text-sm" style={{ color: C.blue }}>Découvrez si le BNC classique vous serait plus avantageux →</div>
                    <div className="text-xs" style={{ color: C.muted }}>Comparez votre situation actuelle avec le régime BNC classique dans notre simulateur.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Encart optimisation pour BNC classique + établie */}
            {!isMicroBNC && isEtablie && (
              <div className="rounded-2xl p-4 border" style={{ backgroundColor: "#F5F0FF", borderColor: "#7C3AED" }}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <div className="font-bold text-sm" style={{ color: "#7C3AED" }}>Optimisation — Contrat Madelin Retraite</div>
                    <div className="text-xs" style={{ color: C.muted }}>En BNC classique, les cotisations Madelin retraite sont déductibles de votre bénéfice. Pensez-y pour optimiser votre fiscalité.</div>
                  </div>
                </div>
              </div>
            )}

            {/* Le saviez-vous — débutante */}
            {isDebutante && (
              <div className="rounded-2xl p-4 border" style={{ backgroundColor: "#F0FFF4", borderColor: "#38A169" }}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💡</span>
                  <div>
                    <div className="font-bold text-sm" style={{ color: "#38A169" }}>Le saviez-vous ?</div>
                    <div className="text-xs" style={{ color: C.navy }}>
                      {isMicroBNC ? [
                        "En Micro-BNC, l'État déduit automatiquement 34% de votre CA avant de calculer vos impôts — sans que vous n'ayez à justifier vos dépenses. C'est la simplification administrative du régime.",
                        "Les cotisations CIPAV des psychologues libéraux sont de 23,2% du CA encaissé (+ 0,2% CFP = 23,4% total, taux 2026), à déclarer chaque trimestre sur autoentrepreneur.urssaf.fr.",
                        "Vous pouvez exercer en libéral sans numéro ADELI si vous n'utilisez pas le titre de psychologue. Vérifiez votre situation auprès de l'ARS."
                      ][Math.floor(Date.now() / 86400000) % 3] : [
                        "En BNC classique, toutes vos charges professionnelles sont déductibles : loyer, supervision, formations, assurances, téléphone, déplacements…",
                        "Les cotisations URSSAF en BNC classique sont calculées par l'URSSAF sur votre bénéfice net déclaré, avec des taux progressifs (maladie, retraite, allocations familiales). Taux moyen constaté : 35% à 45% du bénéfice net.",
                        "Le contrat Madelin vous permet de déduire vos cotisations retraite complémentaire de ce sur quoi vous payez l'impôt. Parlez-en à votre comptable."
                      ][Math.floor(Date.now() / 86400000) % 3]}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Card>
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-bold" style={{ color: C.navy }}>CA mensuel {currentYear}</div>
                {onboardingData.targetCa > 0 && stats.parMois.some((m: any) => m.isProjection) && (
                  <div className="flex items-center gap-2 text-[10px]" style={{ color: C.muted }}>
                    <span style={{ display: "inline-block", width: 16, borderTop: `2px dashed ${C.teal}` }} />
                    Projection basée sur votre profil
                  </div>
                )}
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.parMois} margin={{ top: 0, right: 0, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="mois" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} width={65} tickFormatter={(v: number) => new Intl.NumberFormat('fr-FR').format(v) + ' €'} />
                  <Tooltip content={<CustomTooltip />} />
                  {/* Ghost projection bars for months with no real data */}
                  <Bar dataKey="projection" name="Projection" fill="#E0E7EF" radius={[4,4,0,0]} maxBarSize={40} stroke={C.teal} strokeDasharray="4 3" strokeWidth={1.5} />
                  <Bar dataKey="ca" name="CA brut" fill={C.blue} radius={[4,4,0,0]} maxBarSize={40} />
                  <Bar dataKey="net" name="Net estimé" fill={C.teal} radius={[4,4,0,0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* ── RENTABILITÉ RÉELLE ── */}
            {(() => {
              const nbSeances = Number(seancesMois) || 0;
              const dureeH = Number(dureeMoyenne) === 45 ? 0.75 : 1;
              const caMoisActuel = stats.parMois[currentMonth].ca;
              const chargesMois = isMicroBNC ? 0 : (stats.chargesDeduites > 0 ? stats.chargesDeduites / 12 : 0);
              const netMois = caMoisActuel - caMoisActuel * TAUX_URSSAF - chargesMois;
              const parSeance = nbSeances > 0 ? netMois / nbSeances : 0;
              const parHeure = nbSeances > 0 ? netMois / (nbSeances * dureeH) : 0;
              const netAnnuel = stats.revenuNet;
              const parSeanceAnnuelle = seancesAnnuelles > 0 ? netAnnuel / seancesAnnuelles : 0;
              const hasSeances = nbSeances > 0;
              const moisAvecRecettes = stats.parMois.filter(m => m.ca > 0).length;

              return (
                <div className="space-y-4">
                  <div className="text-sm font-bold" style={{ color: C.navy }}>🧠 Votre rentabilité réelle</div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Card>
                      {hasSeances ? (
                        <>
                          <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: C.muted }}>Par séance ce mois</div>
                          <div className="text-3xl font-bold" style={{ color: C.teal }}>{fmtDec(parSeance)}</div>
                          <div className="text-xs mt-1" style={{ color: C.muted }}>Net après URSSAF{isMicroBNC ? "" : " et charges"} · {nbSeances} séance{nbSeances > 1 ? "s" : ""}</div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-4 text-center">
                          <span className="text-3xl opacity-30 mb-2">🧠</span>
                          <div className="text-xs" style={{ color: C.muted }}>Saisissez vos séances dans l'onglet Saisir pour découvrir ce que vous gagnez vraiment par heure de travail</div>
                        </div>
                      )}
                    </Card>
                    <Card>
                      {hasSeances ? (
                        <>
                          <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: C.muted }}>Par heure travaillée</div>
                          <div className="text-3xl font-bold" style={{ color: C.navy }}>{fmtDec(parHeure)}</div>
                          <div className="text-xs mt-1" style={{ color: C.muted }}>Taux horaire net · séances de {Number(dureeMoyenne)} min</div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-4 text-center">
                          <span className="text-3xl opacity-30 mb-2">🧠</span>
                          <div className="text-xs" style={{ color: C.muted }}>Saisissez vos séances dans l'onglet Saisir pour découvrir ce que vous gagnez vraiment par heure de travail</div>
                        </div>
                      )}
                    </Card>
                    <Card>
                      {(() => {
                        if (seancesAnnuelles > 0 && moisAvecRecettes >= 3) {
                          return (
                            <>
                              <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: C.muted }}>Sur l'année</div>
                              <div className="text-3xl font-bold" style={{ color: C.blue }}>{fmtDec(parSeanceAnnuelle)}</div>
                              <div className="text-xs mt-1" style={{ color: C.muted }}>Moyenne annuelle par séance</div>
                              <div className="text-xs mt-1" style={{ color: C.muted }}>Basé sur {moisAvecRecettes} mois de données</div>
                            </>
                          );
                        } else if (seancesAnnuelles > 0 && moisAvecRecettes < 3) {
                          return (
                            <div className="flex flex-col items-center justify-center py-3 text-center">
                              <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: C.muted }}>Sur l'année</div>
                              <div className="text-xs italic mb-3" style={{ color: C.muted }}>Disponible après 3 mois d'utilisation</div>
                              <div className="w-full h-2 rounded-full overflow-hidden mb-2" style={{ backgroundColor: C.border }}>
                                <div className="h-full rounded-full transition-all" style={{ width: `${(moisAvecRecettes / 3) * 100}%`, backgroundColor: C.teal }} />
                              </div>
                              <div className="text-xs font-medium" style={{ color: C.teal }}>{moisAvecRecettes} mois sur 3</div>
                            </div>
                          );
                        } else {
                          return (
                            <div className="flex flex-col items-center justify-center py-4 text-center">
                              <span className="text-3xl opacity-30 mb-2">🧠</span>
                              <div className="text-xs" style={{ color: C.muted }}>Saisissez vos séances dans l'onglet Saisir pour découvrir ce que vous gagnez vraiment par heure de travail</div>
                            </div>
                          );
                        }
                      })()}
                    </Card>
                  </div>
                  {hasSeances && (
                    <div className="rounded-2xl px-5 py-3 text-sm" style={{
                      backgroundColor: parHeure > 80 ? C.lightTeal : parHeure >= 50 ? "#FFF8F0" : "#FFF0F1",
                      color: parHeure > 80 ? C.teal : parHeure >= 50 ? "#C46200" : C.danger
                    }}>
                      {parHeure > 80
                        ? "✅ Votre rentabilité est solide."
                        : parHeure >= 50
                        ? "💡 Votre rentabilité est correcte — quelques ajustements pourraient l'améliorer."
                        : "⚠️ Votre taux horaire net est en dessous de la moyenne — consultez le simulateur fiscal pour optimiser."}
                    </div>
                  )}
                </div>
              );
            })()}



            {/* ── CTA SIMULER MON ACTIVITÉ ── */}
            {stats.ca > 0 && (
              <button
                onClick={() => setActiveTab("projection")}
                className="w-full rounded-2xl p-4 flex items-center justify-between gap-3 transition-all hover:shadow-lg active:scale-[0.99] text-left"
                style={{ background: `linear-gradient(135deg, ${C.navy} 0%, #2E5F8A 100%)` }}
              >
                <div>
                  <div className="font-bold text-base mb-1" style={{ color: C.white }}>
                    🔮 Simuler mon activité →
                  </div>
                  <div className="text-xs" style={{ color: "#94A3B8" }}>
                    +2 patients, congés d'été, changement de tarif — voyez l'impact exact sur votre revenu net
                  </div>
                </div>
                <div
                  className="flex-shrink-0 text-xs font-bold px-3 py-2 rounded-xl whitespace-nowrap"
                  style={{ backgroundColor: C.teal, color: C.white }}
                >
                  Ouvrir →
                </div>
              </button>
            )}

            <Card>
              <div className="text-sm font-bold mb-4" style={{ color: C.navy }}>
                {isMicroBNC ? "Projection cumulative — CA vs Objectif vs Seuil" : "Projection cumulative — CA vs Objectif"}
              </div>
              {stats.ca > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={stats.cumulMensuel} margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                    <XAxis dataKey="mois" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} width={65} tickFormatter={(v: number) => new Intl.NumberFormat('fr-FR').format(v) + ' €'} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="cumul" name="CA cumulé" stroke={C.blue} strokeWidth={2.5} dot={{ r: 2, fill: C.blue }} connectNulls />
                    <Line type="monotone" dataKey="objectif" name="Objectif" stroke={C.teal} strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
                    {isMicroBNC && <Line type="monotone" dataKey="seuil" name="Seuil légal" stroke={C.danger} strokeWidth={1.5} strokeDasharray="4 2" dot={false} />}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-52 flex items-center justify-center text-sm" style={{ color: C.muted }}>
                  Saisissez vos premières recettes pour voir ce graphique
                </div>
              )}
            </Card>

            <Card>
              <div className="text-sm font-bold mb-4" style={{ color: C.navy }}>Récapitulatif mensuel</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {["Mois","CA brut","Cotisations CIPAV (23,4%)","Ce que vous touchez", isMicroBNC ? "Déduction légale (34%)" : "Charges réelles"].map(h => (
                        <th key={h} className="pb-2 text-left font-semibold" style={{ color: C.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stats.parMois.map((m, i) => (
                      <tr key={i} className="border-b" style={{ borderColor: C.border, opacity: m.ca === 0 ? 0.4 : 1 }}>
                        <td className="py-2 font-medium" style={{ color: C.navy }}>{MOIS_FULL[i]}</td>
                        <td className="py-2 font-bold" style={{ color: C.navy }}>{m.ca > 0 ? fmt(m.ca) : "—"}</td>
                        <td className="py-2" style={{ color: C.blue }}>{m.ca > 0 ? fmt(m.urssaf) : "—"}</td>
                        <td className="py-2 font-semibold" style={{ color: C.teal }}>{m.ca > 0 ? fmt(m.net) : "—"}</td>
                        <td className="py-2" style={{ color: C.muted }}>{m.ca > 0 ? (isMicroBNC ? fmt(m.ca * ABATTEMENT) : fmt(stats.chargesDeduites / Math.max(1, stats.parMois.filter(x => x.ca > 0).length))) : "—"}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: `2px solid ${C.navy}` }}>
                      <td className="pt-3 font-bold" style={{ color: C.navy }}>TOTAL</td>
                      <td className="pt-3 font-bold" style={{ color: C.navy }}>{fmt(stats.ca)}</td>
                      <td className="pt-3 font-bold" style={{ color: C.blue }}>{fmt(stats.urssaf)}</td>
                      <td className="pt-3 font-bold" style={{ color: C.teal }}>{fmt(stats.revenuNet)}</td>
                      <td className="pt-3 font-bold" style={{ color: C.muted }}>{isMicroBNC ? fmt(stats.ca * ABATTEMENT) : fmt(stats.chargesDeduites)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            {/* ── PROGRESSION ANNUELLE ── */}
            <ProgressionAnnuelle />

              </div>{/* fin zone-détails space-y-5 */}
            </div>{/* fin zone-détails collapsible */}

            {/* ── CTA MODE COMPLET (Essentiel uniquement, CA > 5000) ── */}
            {mode === "debutante" && stats.ca > 5000 && (
              <div className="rounded-2xl p-5 flex items-center justify-between gap-4" style={{ backgroundColor: C.navy }}>
                <div className="text-sm font-medium" style={{ color: C.white }}>
                  💡 Prête à aller plus loin ? Débloquez le Simulateur et la Projection
                </div>
                <button
                  onClick={() => setMode("etablie")}
                  className="flex-shrink-0 text-xs font-bold px-4 py-2 rounded-xl border-2 border-white whitespace-nowrap transition-colors hover:bg-white/10"
                  style={{ color: C.white }}
                >
                  Passer en Mode Complet →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════ URSSAF ══════════════════ */}
        {activeTab === "urssaf" && (
          <div className="space-y-5">
            <div className="rounded-2xl p-4 border-l-4" style={{ backgroundColor: C.light, borderLeftColor: C.blue }}>
              <div className="font-bold text-sm mb-1" style={{ color: C.blue }}>
                {isMicroBNC ? `📅 Déclarations URSSAF Micro-BNC ${currentYear}` : `📅 Déclarations URSSAF BNC Classique ${currentYear}`}
              </div>
              <div className="text-xs" style={{ color: C.muted }}>
                {isMicroBNC
                  ? "Vous devez déclarer votre CA et payer vos cotisations chaque trimestre. Taux CIPAV : 23,2% du CA encaissé + 0,2% CFP = 23,4% total (taux officiel 2026)."
                  : "En BNC classique, vos cotisations CIPAV sont calculées sur votre bénéfice net (recettes - charges réelles). Fourchette estimée : 35–45% du bénéfice selon votre niveau de revenus — votre expert-comptable affinera ce montant."}
              </div>
            </div>
            {!isMicroBNC && Number(chargesReelles) > 0 && (
              <Card>
                <div className="font-bold text-sm mb-2" style={{ color: C.navy }}>💼 Vos charges déductibles</div>
                <div className="text-sm" style={{ color: C.muted }}>Total des charges réelles saisies : <strong style={{ color: C.navy }}>{fmt(Number(chargesReelles))}</strong></div>
                <div className="text-xs mt-1" style={{ color: C.muted }}>Ces charges réduisent ce sur quoi vous payez l'impôt et vos cotisations URSSAF.</div>
              </Card>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {stats.parTrimestre.map((t, i) => (
                <Card key={i} className="border-l-4" style={{ borderLeftColor: t.estCourant ? C.teal : C.border }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-bold text-lg" style={{ color: C.navy }}>{t.label}</span>
                      {t.estCourant && <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: C.teal, color: "white" }}>Trimestre actuel</span>}
                    </div>
                    <span className="text-xs" style={{ color: C.muted }}>Échéance : {t.deadline}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[["CA", fmt(t.ca), C.navy], ["URSSAF", fmt(t.urssaf), C.blue], ["Net", fmt(t.ca - t.urssaf), C.teal]].map(([l,v,c]) => (
                      <div key={l} className="rounded-xl p-3" style={{ backgroundColor: C.bg }}>
                        <div className="text-xs" style={{ color: C.muted }}>{l}</div>
                        <div className="font-bold text-sm mt-1" style={{ color: c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs px-3 py-2 rounded-xl" style={{ backgroundColor: t.ca > 0 ? C.lightTeal : "#FFF8F0", color: t.ca > 0 ? C.teal : C.warning }}>
                    {t.ca > 0 ? `✅ À déclarer sur autoentrepreneur.urssaf.fr — Montant URSSAF : ${fmt(t.urssaf)}` : "⏳ Aucune recette saisie pour ce trimestre"}
                  </div>
                </Card>
              ))}
            </div>
            <Card className="border-l-4" style={{ borderLeftColor: C.navy, backgroundColor: C.light }}>
              <div className="font-bold text-sm mb-3" style={{ color: C.navy }}>
                {isMicroBNC ? "📋 Comment déclarer sur autoentrepreneur.urssaf.fr" : "📋 Comment déclarer en BNC classique"}
              </div>
              <div className="space-y-2 text-xs" style={{ color: C.navy }}>
                {(isMicroBNC
                  ? ["Connectez-vous sur autoentrepreneur.urssaf.fr avec vos identifiants","Cliquez sur « Déclarer mon chiffre d'affaires »","Saisissez le CA encaissé sur le trimestre (pas les factures émises)","Les cotisations CIPAV sont calculées automatiquement (23,4% total)","Payez en ligne par prélèvement ou carte bancaire"]
                  : ["Tenez un livre-journal de vos recettes et dépenses professionnelles","Conservez tous vos justificatifs de charges (factures, tickets, relevés)","Votre expert-comptable établit votre déclaration 2035 annuelle","Les cotisations URSSAF sont calculées sur votre bénéfice net","Payez vos acomptes trimestriels ou mensuels selon votre choix"]
                ).map((s,i) => (
                  <div key={i} className="flex gap-2"><span className="font-bold" style={{ color: C.teal }}>{i+1}.</span><span>{s}</span></div>
                ))}
              </div>
            </Card>
            {/* Récap annuel intégré — total URSSAF + net après toutes déductions */}
            <Card>
              <div className="font-bold text-sm mb-3" style={{ color: C.navy }}>💰 Total {currentYear}</div>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { label: "CA encaissé",        val: stats.ca,         color: C.navy },
                  { label: "URSSAF / CIPAV",      val: stats.urssaf,     color: C.blue },
                  { label: "Vrai net (IR inclus)", val: stats.revenuNet,  color: C.teal },
                ].map(({ label, val, color }) => (
                  <div key={label} className="rounded-xl p-3" style={{ backgroundColor: C.bg }}>
                    <div className="text-[10px] font-semibold" style={{ color: C.muted }}>{label}</div>
                    <div className="font-bold text-base mt-1" style={{ color }}>{fmt(val)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[11px] text-center" style={{ color: C.muted }}>
                IR estimé : {fmt(stats.ir)} · Taux effectif : {stats.ca > 0 ? Math.round(stats.ir / stats.ca * 100) : 0}% du CA
              </div>
            </Card>

            {/* ── CALENDRIER FISCAL ── */}
            <CalendrierFiscal isMicroBNC={isMicroBNC} />
          </div>
        )}

        {/* ══════════════════ OBJECTIF ══════════════════ */}
        {activeTab === "objectif" && (
          <div className="max-w-lg mx-auto space-y-5">
            <Card>
              <div className="font-bold text-base mb-4" style={{ color: C.navy }}>🎯 Définir votre objectif de CA {currentYear}</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: C.muted }}>OBJECTIF CA ANNUEL (€)</label>
                  <input type="number" value={objectifInput} onChange={e => setObjectifInput(e.target.value)}
                    placeholder="ex: 50000" min="0" step="100"
                    className="w-full rounded-xl border px-4 py-3 text-base font-bold outline-none"
                    style={{ borderColor: C.border, color: C.navy }} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[30000, 50000, 60000].map(v => (
                    <button key={v} onClick={() => setObjectifInput(String(v))}
                      className="py-2 rounded-xl text-xs font-semibold border"
                      style={{ borderColor: C.border, color: C.navy, backgroundColor: Number(objectifInput) === v ? C.light : C.white }}>
                      {fmt(v)}
                    </button>
                  ))}
                </div>
                <button onClick={() => { const v = Number(objectifInput); if(v>0){setObjectifCA(v);saveObjectif(v);} }}
                  className="w-full py-3 rounded-xl text-white font-bold text-sm"
                  style={{ backgroundColor: C.teal }}>
                  Enregistrer l'objectif
                </button>
              </div>
            </Card>
            <Card>
              <div className="font-bold text-sm mb-4" style={{ color: C.navy }}>Progression vers l'objectif</div>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold mb-1" style={{ color: objectifColor }}>{stats.pctObjectif.toFixed(1)}%</div>
                <div className="text-sm" style={{ color: C.muted }}>{fmt(stats.ca)} / {fmt(objectifCA)}</div>
              </div>
              <div className="w-full h-6 rounded-full overflow-hidden mb-3" style={{ backgroundColor: C.border }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${stats.pctObjectif}%`, backgroundColor: objectifColor }} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-center text-xs">
                <div className="rounded-xl p-3" style={{ backgroundColor: C.bg }}>
                  <div style={{ color: C.muted }}>Restant à réaliser</div>
                  <div className="font-bold text-base mt-1" style={{ color: C.navy }}>{fmt(Math.max(0, objectifCA - stats.ca))}</div>
                </div>
                <div className="rounded-xl p-3" style={{ backgroundColor: C.bg }}>
                  <div style={{ color: C.muted }}>Par mois restant</div>
                  <div className="font-bold text-base mt-1" style={{ color: C.blue }}>
                    {fmt(Math.max(0, objectifCA - stats.ca) / Math.max(1, 12 - currentMonth))}
                  </div>
                </div>
              </div>
              {stats.projectionFin >= objectifCA && (
                <div className="mt-3 text-xs px-3 py-2 rounded-xl text-center" style={{ backgroundColor: C.lightTeal, color: C.teal }}>
                  🎉 Votre projection ({fmt(stats.projectionFin)}) dépasse votre objectif !
                </div>
              )}
              {stats.projectionFin < objectifCA && stats.ca > 0 && (
                <div className="mt-3 text-xs px-3 py-2 rounded-xl text-center" style={{ backgroundColor: "#FFF8F0", color: C.warning }}>
                  📈 Projection actuelle : {fmt(stats.projectionFin)} — Il manque {fmt(objectifCA - stats.projectionFin)} pour atteindre l'objectif
                </div>
              )}
            </Card>
          </div>
        )}

        {/* ══════════════════ SAISIE ══════════════════ */}
        {activeTab === "saisie" && (() => {
          const devRecettesOverride = (import.meta.env.DEV && typeof window !== "undefined")
            ? (window as any).__DEV_RECETTES_OVERRIDE__ as number | null | undefined
            : undefined;
          const recettesCeMois = devRecettesOverride != null
            ? devRecettesOverride
            : recettes.filter(r => {
                const d = new Date(r.date);
                return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
              }).length;
          const saisieLimitReached = isEssentiel && recettesCeMois >= 10;

          return (
          <div className="max-w-lg mx-auto space-y-4">
            <Card>
              <div className="font-bold text-base mb-4" style={{ color: C.navy }}>➕ Saisir mes recettes — {MOIS_FULL[currentMonth]} {currentYear}</div>

              {/* Jauge progression recettes — Plan Essentiel uniquement */}
              {isEssentiel && recettesCeMois < 10 && (
                <div className="mb-4 rounded-xl p-3" style={{ backgroundColor: C.bg }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold" style={{ color: recettesCeMois >= 8 ? C.warning : C.muted }}>
                      {recettesCeMois >= 8 && "⚠️ "}{recettesCeMois} / 10 recettes gratuites ce mois-ci
                    </span>
                    {recettesCeMois >= 8 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${C.warning}20`, color: C.warning }}>
                        Bientôt atteint
                      </span>
                    )}
                  </div>
                  <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${recettesCeMois >= 8 ? C.warning : C.teal}15` }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(recettesCeMois / 10) * 100}%`,
                        backgroundColor: recettesCeMois >= 8 ? C.warning : C.teal,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Limite Essentiel */}
              {saisieLimitReached && (
                <div className="rounded-2xl p-5 mb-4 flex flex-col gap-3 text-center" style={{ backgroundColor: C.navy }}>
                  <span className="text-3xl">🚀</span>
                  <p className="text-sm font-bold" style={{ color: C.white }}>
                    Vous avez atteint votre limite de 10 recettes gratuites ce mois-ci.
                  </p>
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
                    Votre activité se développe ! Passez au plan Pro pour une saisie illimitée.
                  </p>
                  <button
                    onClick={() => { window.location.href = "/pricing"; }}
                    className="self-center px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: C.teal }}
                  >
                    {proCta}
                  </button>
                </div>
              )}

              {/* Toggle mode — Lot | CSV */}
              <div className="flex rounded-xl p-1 mb-5" style={{ backgroundColor: C.bg }}>
                {([
                  { id: "lot", label: "📦 Par lot" },
                  { id: "csv", label: "📁 CSV" },
                ] as const).map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setSaisieMode(id)}
                    className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-200"
                    style={{
                      backgroundColor: saisieMode === id ? C.white : "transparent",
                      color: saisieMode === id ? C.navy : C.muted,
                      boxShadow: saisieMode === id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* ── MODE LOT ── */}
              {saisieMode === "lot" && (() => {
                const lotNb = Number(lotSeances) || 0;
                const lotPrice = Number(lotTarif) || 0;
                const lotTotal = lotNb * lotPrice;
                const lotUrssaf = isMicroBNC
                  ? Math.round(lotTotal * TAUX_URSSAF)
                  : Math.round(Math.max(0, lotTotal - stats.chargesDeduites / 12) * TAUX_URSSAF_BNC);
                const lotNet = lotTotal - lotUrssaf;
                const canAddLot = lotNb > 0 && lotPrice > 0;
                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold mb-1" style={{ color: C.muted }}>SÉANCES CE MOIS</label>
                        <input
                          type="number" min="0" placeholder="ex : 85"
                          value={lotSeances} onChange={e => setLotSeances(e.target.value)}
                          className="w-full rounded-xl border px-4 py-2.5 text-sm font-bold outline-none"
                          style={{ borderColor: lotSeances ? C.teal : C.border, color: C.navy,
                            backgroundColor: lotSeances ? C.lightTeal : C.white }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold mb-1" style={{ color: C.muted }}>TARIF / SÉANCE</label>
                        <div className="relative">
                          <input
                            type="number" min="0" placeholder="ex : 70"
                            value={lotTarif} onChange={e => setLotTarif(e.target.value)}
                            className="w-full rounded-xl border px-4 py-2.5 text-sm font-bold outline-none pr-8"
                            style={{ borderColor: lotTarif ? C.teal : C.border, color: C.navy,
                              backgroundColor: lotTarif ? C.lightTeal : C.white }}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold" style={{ color: C.muted }}>€</span>
                        </div>
                      </div>
                    </div>

                    {/* Calcul temps réel */}
                    <div className="rounded-xl p-4 transition-all duration-200"
                      style={{ backgroundColor: canAddLot ? C.lightTeal : C.bg, border: `1px solid ${canAddLot ? C.teal : C.border}` }}>
                      {canAddLot ? (
                        <>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-bold" style={{ color: C.navy }}>
                              {lotNb} × {lotPrice}€
                            </span>
                            <span className="text-xl font-bold" style={{ color: C.navy }}>{fmt(lotTotal)}</span>
                          </div>
                          <div className="flex justify-between text-xs" style={{ color: C.muted }}>
                            <span>URSSAF estimée</span>
                            <span>− {fmt(lotUrssaf)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-bold mt-1 pt-1 border-t" style={{ borderColor: C.border }}>
                            <span style={{ color: C.teal }}>Net estimé</span>
                            <span style={{ color: C.teal }}>{fmt(lotNet)}</span>
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-center py-1" style={{ color: C.muted }}>
                          Renseignez le nombre de séances et votre tarif ✨
                        </p>
                      )}
                    </div>

                    <button
                      onClick={handleAddLot}
                      disabled={!canAddLot || saisieLimitReached}
                      className="w-full py-3 rounded-xl text-white font-bold text-sm transition-opacity"
                      style={{ backgroundColor: C.teal, opacity: (canAddLot && !saisieLimitReached) ? 1 : 0.4, cursor: (canAddLot && !saisieLimitReached) ? "pointer" : "not-allowed" }}
                    >
                      {canAddLot ? `Enregistrer ${lotNb} séances (${fmt(lotTotal)}) →` : "Renseignez vos séances pour continuer"}
                    </button>
                    <p className="text-xs text-center" style={{ color: C.muted }}>
                      Crée une entrée mensuelle · Séances et durée sauvegardées automatiquement
                    </p>

                    {/* ── Saisie manuelle (accordéon avancé) ── */}
                    <button
                      type="button"
                      onClick={() => setManualOpen(o => !o)}
                      className="w-full flex items-center justify-between text-xs font-semibold px-3 py-2 rounded-xl border transition-all mt-2"
                      style={{ borderColor: C.border, color: C.muted, backgroundColor: C.bg }}
                    >
                      <span>🖊️ Saisie manuelle (avancé)</span>
                      <span>{manualOpen ? "▴" : "▾"}</span>
                    </button>
                    {manualOpen && (
                      <div className="space-y-4 pt-2">
                        <div>
                          <label className="block text-xs font-semibold mb-1" style={{ color: C.muted }}>DATE DE PERCEPTION</label>
                          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                            className="w-full rounded-xl border px-4 py-2 text-sm outline-none"
                            style={{ borderColor: C.border, color: C.navy }} />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1" style={{ color: C.muted }}>MONTANT (€)</label>
                          <input type="number" placeholder="ex : 1800" value={form.montant}
                            onChange={e => setForm(f => ({ ...f, montant: e.target.value }))} min="0" step="0.01"
                            className="w-full rounded-xl border px-4 py-2 text-sm outline-none"
                            style={{ borderColor: C.border, color: C.navy }} />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1" style={{ color: C.muted }}>TYPE DE RECETTE</label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: "Consultation",  icon: "🧠" },
                              { value: "Supervision",   icon: "👥" },
                              { value: "Formation",     icon: "📚" },
                              { value: "Expertise",     icon: "⚖️" },
                              { value: "Autre",         icon: "📋" },
                            ].map(({ value, icon }) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setForm(f => ({ ...f, description: value }))}
                                className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
                                style={{
                                  borderColor: form.description === value ? C.teal : C.border,
                                  backgroundColor: form.description === value ? C.lightTeal : C.white,
                                  color: form.description === value ? C.teal : C.navy,
                                }}
                              >
                                {icon} {value}
                              </button>
                            ))}
                          </div>
                          {form.description === "Autre" && (
                            <input
                              type="text"
                              placeholder="Précisez..."
                              className="mt-2 w-full rounded-xl border px-4 py-2 text-sm outline-none"
                              style={{ borderColor: C.border, color: C.navy }}
                              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                            />
                          )}
                        </div>
                        {formError && <div className="text-xs font-medium rounded-lg px-3 py-2" style={{ backgroundColor: "#FEE8EA", color: C.danger }}>{formError}</div>}
                        <button onClick={handleAdd} disabled={saisieLimitReached} className="w-full py-3 rounded-xl text-white font-bold text-sm disabled:opacity-40"
                          style={{ backgroundColor: C.teal }}>Enregistrer la recette</button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ── MODE CSV / DataImporter ── */}
              {saisieMode === "csv" && (
                isEssentiel ? (
                  <div className="rounded-2xl p-5 flex flex-col gap-3 text-center" style={{ backgroundColor: C.navy }}>
                    <span className="text-3xl">📁</span>
                    <p className="text-sm font-bold" style={{ color: C.white }}>Import universel — Plan Pro</p>
                    <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
                      Importez votre relevé Excel, Indy ou Abby. Catégorisation automatique des charges, zéro saisie manuelle.
                    </p>
                    <button onClick={() => { window.location.href = "/pricing"; }}
                      className="self-center px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: C.teal }}>
                      {proCta}
                    </button>
                  </div>
                ) : (
                  <DataImporter
                    tarifRef={profile?.tarif_seance ?? 70}
                    onInsight={data => {
                      setImportInsight(data);
                      setShowImportModal(true);
                      setActiveTab("dashboard");
                    }}
                    onImportRecettes={async (rows) => {
                      if (!user) return;
                      const toInsert = rows.map(r => ({ ...r, user_id: user.id }));
                      const { data, error } = await supabase.from("recettes").insert(toInsert).select();
                      if (error) { toast({ title: "❌ Erreur import", description: error.message, variant: "destructive" }); return; }
                      const inserted = (data ?? []).map((r: any) => ({ id: r.id, date: r.date, montant: r.montant, description: r.description }));
                      setRecettes(prev => [...prev, ...inserted]);
                      toast({ title: `✅ ${rows.length} recette${rows.length > 1 ? "s" : ""} importée${rows.length > 1 ? "s" : ""}`, description: "Tableau de bord mis à jour.", duration: 3000 });
                      setSaisieMode("lot");
                    }}
                    onClose={() => setSaisieMode("lot")}
                  />
                )
              )}
            </Card>

            {/* ── SÉANCES DU MOIS ── */}
            <Card>
              <div className="font-bold text-base" style={{ color: C.navy }}>⏱ Analyse de votre temps de travail — {MOIS_FULL[currentMonth]}</div>
              <p className="text-xs mt-1 mb-4" style={{ color: C.muted }}>
                Indiquez vos séances pour calculer votre revenu net par heure travaillée.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: C.muted }}>Combien de séances avez-vous effectué ?</label>
                  <input type="number" placeholder="ex : 18 séances" value={seancesMois}
                    onChange={e => { setSeancesMois(e.target.value); const n = Number(e.target.value); if (n >= 0) saveSeances(n, Number(dureeMoyenne)); }} min="0" step="1"
                    className="w-full rounded-xl border px-4 py-2 text-sm outline-none"
                    style={{ borderColor: C.border, color: C.navy }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-2" style={{ color: C.muted }}>⏱️ DURÉE MOYENNE D'UNE SÉANCE</label>
                  <div className="flex gap-3">
                    {["45", "60"].map(d => (
                      <button key={d} onClick={() => { setDureeMoyenne(d); if (Number(seancesMois) > 0) saveSeances(Number(seancesMois), Number(d)); }}
                        className="flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors"
                        style={{
                          borderColor: dureeMoyenne === d ? C.teal : C.border,
                          backgroundColor: dureeMoyenne === d ? C.lightTeal : C.white,
                          color: dureeMoyenne === d ? C.teal : C.navy
                        }}>
                        {d} min
                      </button>
                    ))}
                  </div>
                </div>
                {(() => {
                  const nb = Number(seancesMois) || 0;
                  const dureeH = Number(dureeMoyenne) === 45 ? 0.75 : 1;
                  // Même formule que la StatCard : revenuNet (CIPAV + IR inclus) ÷ séances totales
                  // seancesAnnuelles = total DB (peut être 0 si pas encore sauvegardé)
                  // → fallback sur nb (valeur saisie immédiatement dans le state)
                  const totalSeances = seancesAnnuelles > 0 ? seancesAnnuelles : nb;
                  const netHoraire = nb > 0 && stats.revenuNet > 0 && totalSeances > 0
                    ? Math.round(stats.revenuNet / (totalSeances * dureeH))
                    : null;
                  if (netHoraire === null) return null;
                  return (
                    <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ backgroundColor: C.lightTeal, border: `1px solid ${C.teal}33` }}>
                      <span className="text-sm font-semibold" style={{ color: C.navy }}>Votre net horaire</span>
                      <span className="text-lg font-bold" style={{ color: C.teal }}>{netHoraire} €/heure</span>
                    </div>
                  );
                })()}
                <div className="text-xs opacity-70" style={{ color: C.muted }}>
                  Ces données restent privées et servent uniquement à calculer votre rentabilité réelle.
                </div>
              </div>
            </Card>

            {form.montant && !isNaN(Number(form.montant)) && Number(form.montant) > 0 && (
              <Card className="border-l-4" style={{ borderLeftColor: C.teal }}>
                <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: C.teal }}>Aperçu instantané</div>
                <div className="grid grid-cols-3 gap-3 text-center text-xs">
                  {[
                    ["Montant brut", Number(form.montant), C.navy],
                    ["Cotisations CIPAV (23,4%)", Number(form.montant)*TAUX_URSSAF, C.blue],
                    ["Net avant IR", Number(form.montant) - Number(form.montant)*TAUX_URSSAF, C.teal]
                  ].map(([l,v,c]) => (
                    <div key={l} className="rounded-xl p-3" style={{ backgroundColor: C.light }}>
                      <div style={{ color: C.muted }}>{l}</div>
                      <div className="font-bold text-base mt-1" style={{ color: c as string }}>{fmtDec(v as number)}</div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
          );
        })()}

        {/* ══════════════════ HISTORIQUE ══════════════════ */}
        {activeTab === "historique" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold" style={{ color: C.navy }}>
                {recettesFiltrees.length} recette{recettesFiltrees.length > 1 ? "s" : ""} — {fmt(recettesFiltrees.reduce((s,r) => s+Number(r.montant),0))}
              </div>
              <select value={filterMois} onChange={e => setFilterMois(e.target.value)}
                className="text-xs rounded-xl border px-3 py-2 outline-none"
                style={{ borderColor: C.border, color: C.navy, backgroundColor: C.white }}>
                <option value="all">Toutes les périodes</option>
                {MOIS_FULL.map((m,i) => <option key={i} value={i}>{m} {currentYear}</option>)}
              </select>
            </div>
            <Card className="p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: C.light }}>
                  <tr>{["Date","Description","Montant","URSSAF","Net avant IR",""].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: C.muted }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {recettesFiltrees.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-10 text-sm" style={{ color: C.muted }}>Aucune recette pour cette période</td></tr>
                  )}
                  {recettesFiltrees.map(r => {
                    const u = Number(r.montant)*TAUX_URSSAF;
                    const n = Number(r.montant) - u;
                    return (
                      <tr key={r.id} className="border-t" style={{ borderColor: C.border }}>
                        <td className="px-4 py-3 text-xs" style={{ color: C.muted }}>{new Date(r.date).toLocaleDateString("fr-FR")}</td>
                        <td className="px-4 py-3 font-medium" style={{ color: C.navy }}>{r.description}</td>
                        <td className="px-4 py-3 font-bold" style={{ color: C.navy }}>{fmt(r.montant)}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: C.blue }}>{fmt(u)}</td>
                        <td className="px-4 py-3 text-xs font-semibold" style={{ color: C.teal }}>{fmt(n)}</td>
                        <td className="px-4 py-3">
                          {showDeleteConfirm === r.id ? (
                            <div className="flex gap-1">
                              <button onClick={() => handleDelete(r.id)} className="text-xs px-2 py-1 rounded-lg" style={{ backgroundColor: C.danger, color: "white" }}>Oui</button>
                              <button onClick={() => setShowDeleteConfirm(null)} className="text-xs px-2 py-1 rounded-lg" style={{ backgroundColor: C.border, color: C.navy }}>Non</button>
                            </div>
                          ) : (
                            <button onClick={() => setShowDeleteConfirm(r.id)} className="text-xs px-2 py-1 rounded-lg" style={{ backgroundColor: "#FEE8EA", color: C.danger }}>✕</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {/* ══════════════════ PROJECTION ══════════════════ */}
        {activeTab === "projection" && (
          <Projection
            caAnnuel={stats.ca}
            revenuNetActuel={stats.revenuNet}
            moyenneMensuelle={stats.moyenneMensuelle}
            onRetour={() => setActiveTab("dashboard")}
            isMicroBNC={isMicroBNC}
            seancesAnnuelles={seancesAnnuelles}
            objectifCA={objectifCA}
            chargesReelles={Number(chargesReelles) || 0}
            tarifActuel={seancesAnnuelles > 0 && stats.ca > 0 ? Math.round(stats.ca / seancesAnnuelles) : 60}
            onNavigateCharges={() => setActiveTab("charges2035")}
            semainesTravaillees={profile?.semaines_travaillees ?? 45}
          />
        )}

        {/* ══════════════════ SIMULATEUR ══════════════════ */}
        {activeTab === "simulateur" && (
          <Simulateur
            caAnnuel={stats.ca}
            onRetour={() => { setActiveTab("dashboard"); setSimulPreFill(null); }}
            initialCharges={simulPreFill?.charges}
            quickSimMode={simulPreFill?.quickMode}
          />
        )}

        {/* ══════════════════ ASSISTANT ══════════════════ */}
        {activeTab === "assistant" && <AssistantChat />}

        {/* ══════════════════ PROFIL ══════════════════ */}
        {activeTab === "profil" && (
          <div className="max-w-lg mx-auto space-y-5">
            <Card>
              <div className="font-bold text-base mb-5" style={{ color: C.navy }}>⚙️ Mon profil fiscal</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: C.muted }}>Régime fiscal</label>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      { value: "micro-bnc", icon: "📋", label: "Micro-BNC" },
                      { value: "bnc-classique", icon: "📊", label: "BNC Classique" },
                    ]).map(opt => (
                      <button key={opt.value}
                        onClick={async () => {
                          if (!user) return;
                          const { error } = await supabase.from("profils").update({ statut_fiscal: opt.value }).eq("user_id", user.id);
                          if (error) { toast({ title: "Erreur", description: error.message, variant: "destructive" }); return; }
                          refetchProfile();
                          toast({ title: "✅ Profil mis à jour", description: `Régime : ${opt.label}`, duration: 2000 });
                        }}
                        className="rounded-xl p-4 border-2 text-center transition-all relative"
                        style={{
                          borderColor: profile?.statut_fiscal === opt.value ? C.teal : C.border,
                          backgroundColor: profile?.statut_fiscal === opt.value ? C.lightTeal : C.white,
                        }}>
                        {profile?.statut_fiscal === opt.value && <span className="absolute top-2 right-2 text-sm">✅</span>}
                        <div className="text-2xl mb-1">{opt.icon}</div>
                        <div className="font-bold text-sm" style={{ color: C.navy }}>{opt.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: C.muted }}>Ancienneté</label>
                  <div className="space-y-2">
                    {([
                      { value: "debutante", icon: "🌱", label: "Je démarre", desc: "Moins de 2 ans" },
                      { value: "developpement", icon: "🌿", label: "En développement", desc: "2 à 5 ans" },
                      { value: "etablie", icon: "🌳", label: "Établie", desc: "Plus de 5 ans" },
                    ]).map(opt => (
                      <button key={opt.value}
                        onClick={async () => {
                          if (!user) return;
                          const { error } = await supabase.from("profils").update({ anciennete: opt.value }).eq("user_id", user.id);
                          if (error) { toast({ title: "Erreur", description: error.message, variant: "destructive" }); return; }
                          refetchProfile();
                          toast({ title: "✅ Profil mis à jour", description: `Ancienneté : ${opt.label}`, duration: 2000 });
                        }}
                        className="w-full text-left rounded-xl p-3 border-2 flex items-center gap-3 transition-all relative"
                        style={{
                          borderColor: profile?.anciennete === opt.value ? C.teal : C.border,
                          backgroundColor: profile?.anciennete === opt.value ? C.lightTeal : C.white,
                        }}>
                        {profile?.anciennete === opt.value && <span className="absolute top-2 right-2 text-sm">✅</span>}
                        <span className="text-xl">{opt.icon}</span>
                        <div>
                          <div className="font-bold text-sm" style={{ color: C.navy }}>{opt.label}</div>
                          <div className="text-xs" style={{ color: C.muted }}>{opt.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 text-xs" style={{ color: C.muted }}>
                Les modifications prennent effet immédiatement sur votre tableau de bord.
              </div>
            </Card>
          </div>
        )}

        {/* ══════════════════ RÉFORME 2026 ══════════════════ */}
        {activeTab === "reforme" && (() => {
          const today = new Date();
          const daysUntil = (d: Date) => Math.ceil((d.getTime() - today.getTime()) / 86_400_000);

          const DEADLINES = [
            {
              id: "declaration",
              icon: "📋",
              label: "Déclaration revenus 2025 en ligne",
              subtitle: "Formulaires n°2042 + 2042C-PRO — Micro-BNC et BNC Classique",
              date: new Date("2026-05-31"),
            },
            {
              id: "cipav",
              icon: "💰",
              label: "Acompte provisionnel CIPAV",
              subtitle: "Basé sur vos revenus 2024 — ajustement possible sur demande",
              date: new Date("2026-09-15"),
            },
            {
              id: "regime",
              icon: "🔄",
              label: "Dernier jour pour changer de régime",
              subtitle: "Changement Micro ↔ BNC Classique applicable au 1er janvier 2027",
              date: new Date("2026-12-31"),
            },
          ];

          // Calculs comparatifs avec les vrais chiffres de l'utilisatrice
          const caRef = stats.ca > 0 ? stats.ca : onboardingData.targetCa * 12;
          const chargesEstim = onboardingData.estCharges > 0 ? onboardingData.estCharges * 12 : 0;
          const chargesReel = isMicroBNC
            ? chargesEstim
            : (stats.chargesDeduites > 0 ? stats.chargesDeduites : chargesEstim);

          // Micro-BNC
          const cipavMicro = Math.round(caRef * 0.234);
          const beneficeMicro = Math.round(caRef * 0.66);
          const irMicro = calculIR(beneficeMicro);
          const netMicro = Math.max(0, caRef - cipavMicro - irMicro);

          // BNC Classique (estimation)
          const beneficeAvantCotisReel = Math.max(0, caRef - chargesReel);
          const cipavReel = Math.round(beneficeAvantCotisReel * 0.40);
          const beneficeImposableReel = Math.max(0, beneficeAvantCotisReel - cipavReel);
          const irReel = calculIR(beneficeImposableReel);
          const netReel = Math.max(0, beneficeImposableReel - irReel);

          const delta = Math.round(netReel - netMicro);
          const isReelBetter = delta > 0;

          // Seuil Micro-BNC 2026
          const projectionCA = stats.projectionFin > 0 ? stats.projectionFin : caRef;
          const distanceSeuil = SEUIL_CA - projectionCA;
          const pctSeuil = Math.min(100, (projectionCA / SEUIL_CA) * 100);
          const seuilColor = pctSeuil > 85 ? C.danger : pctSeuil > 65 ? C.warning : C.teal;

          return (
            <div className="max-w-2xl mx-auto space-y-4">
              <div>
                <h2 className="text-lg font-bold" style={{ color: C.navy }}>🗓️ Réforme & Échéances 2026</h2>
                <p className="text-sm mt-0.5" style={{ color: C.muted }}>
                  Vos prochaines obligations fiscales et l'impact des règles 2026 sur vos chiffres.
                </p>
              </div>

              {/* Deadlines */}
              <Card>
                <div className="font-bold text-sm mb-3" style={{ color: C.navy }}>Prochaines échéances</div>
                <div className="space-y-2">
                  {DEADLINES.map(d => {
                    const days = daysUntil(d.date);
                    const passed = days < 0;
                    const urgColor = passed ? C.muted : days < 30 ? C.danger : days < 75 ? C.warning : C.teal;
                    return (
                      <div
                        key={d.id}
                        className="flex items-start gap-3 p-3 rounded-xl"
                        style={{ backgroundColor: passed ? "#F7F7F7" : urgColor + "12" }}
                      >
                        <span className="text-xl shrink-0 mt-0.5">{d.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-semibold text-sm" style={{ color: passed ? C.muted : C.navy }}>
                                {d.label}
                              </div>
                              <div className="text-[11px] mt-0.5" style={{ color: C.muted }}>{d.subtitle}</div>
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="font-extrabold text-sm" style={{ color: urgColor }}>
                                {passed ? "Passée" : `J−${days}`}
                              </div>
                              <div className="text-[10px]" style={{ color: C.muted }}>
                                {d.date.toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Comparatif Micro vs Réel */}
              {caRef > 0 && (
                <Card>
                  <div className="font-bold text-sm mb-1" style={{ color: C.navy }}>
                    Micro-BNC vs BNC Classique — impact sur vos chiffres
                  </div>
                  <div className="text-[11px] mb-3" style={{ color: C.muted }}>
                    Basé sur {fmt(caRef)} de CA {stats.isEstimation ? "estimé" : "réel"}
                    {chargesReel > 0 ? ` et ${fmt(chargesReel)} de charges` : ""}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      {
                        label: "Micro-BNC",
                        desc: "Abattement forfaitaire 34%",
                        cipav: cipavMicro,
                        ir: irMicro,
                        net: netMicro,
                        isCurrent: isMicroBNC,
                      },
                      {
                        label: "BNC Classique",
                        desc: chargesReel > 0 ? `Charges réelles : ${fmt(chargesReel)}` : "Charges réelles déductibles",
                        cipav: cipavReel,
                        ir: irReel,
                        net: netReel,
                        isCurrent: !isMicroBNC,
                      },
                    ].map(col => (
                      <div
                        key={col.label}
                        className="rounded-xl p-3"
                        style={{
                          backgroundColor: col.isCurrent ? C.lightTeal : C.light,
                          border: col.isCurrent ? `2px solid ${C.teal}` : `1px solid ${C.border}`,
                        }}
                      >
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="text-xs font-bold" style={{ color: col.isCurrent ? C.teal : C.muted }}>
                            {col.label}
                          </span>
                          {col.isCurrent && (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded-full text-white font-bold"
                              style={{ backgroundColor: C.teal }}
                            >
                              Actuel
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] mb-2" style={{ color: C.muted }}>{col.desc}</div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between" style={{ color: C.muted }}>
                            <span>CIPAV</span>
                            <span className="font-semibold" style={{ color: C.navy }}>−{fmt(col.cipav)}</span>
                          </div>
                          <div className="flex justify-between" style={{ color: C.muted }}>
                            <span>IR estimé</span>
                            <span className="font-semibold" style={{ color: C.navy }}>−{fmt(col.ir)}</span>
                          </div>
                          <div
                            className="flex justify-between font-bold pt-1.5 border-t"
                            style={{ borderColor: C.border, color: C.navy }}
                          >
                            <span>Net/an</span>
                            <span>{fmt(col.net)}</span>
                          </div>
                          <div className="flex justify-between text-[10px]" style={{ color: C.muted }}>
                            <span>Net/mois</span>
                            <span className="font-semibold">{fmt(Math.round(col.net / 12))}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {Math.abs(delta) >= 500 && (
                    <button
                      className="mt-3 w-full rounded-xl p-3 text-sm font-bold text-center transition-opacity hover:opacity-90"
                      style={{
                        backgroundColor: isReelBetter ? C.lightTeal : "#FFF3E0",
                        color: isReelBetter ? C.teal : C.warning,
                      }}
                      onClick={() => {
                        setSimulPreFill({ charges: { autres: chargesReel }, quickMode: true });
                        setActiveTab("simulateur");
                      }}
                    >
                      {isReelBetter
                        ? `Le BNC Classique vous rapporterait ${fmt(delta)}/an de plus — Simuler →`
                        : `Le Micro-BNC vous avantage de ${fmt(Math.abs(delta))}/an — Confirmer avec le simulateur →`}
                    </button>
                  )}
                </Card>
              )}

              {/* Seuil Micro-BNC */}
              {isMicroBNC && (
                <Card>
                  <div className="font-bold text-sm mb-2" style={{ color: C.navy }}>
                    Seuil Micro-BNC 2026 — {fmt(SEUIL_CA)}
                  </div>
                  <div className="w-full h-3 rounded-full overflow-hidden mb-2" style={{ backgroundColor: C.border }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pctSeuil}%`, backgroundColor: seuilColor }}
                    />
                  </div>
                  <div className="flex justify-between text-xs mb-2" style={{ color: C.muted }}>
                    <span>CA projeté : <strong style={{ color: C.navy }}>{fmt(projectionCA)}</strong></span>
                    <span style={{ color: seuilColor }}>
                      {distanceSeuil > 0 ? `Marge : ${fmt(distanceSeuil)}` : "⚠️ Seuil dépassé"}
                    </span>
                  </div>
                  {distanceSeuil > 0 && distanceSeuil < 15_000 && (
                    <div className="text-[11px] p-2.5 rounded-lg" style={{ backgroundColor: "#FFF3E0", color: C.warning }}>
                      ⚠️ Marge de {fmt(distanceSeuil)} avant le seuil. Si votre CA le dépasse deux années consécutives, le BNC Classique devient obligatoire.
                    </div>
                  )}
                  {distanceSeuil <= 0 && (
                    <div className="text-[11px] p-2.5 rounded-lg" style={{ backgroundColor: "#FFF0F1", color: C.danger }}>
                      🚨 Seuil 2026 dépassé. Si c'était déjà le cas en 2025, le BNC Classique est obligatoire dès 2027 — consultez votre onglet Profil.
                    </div>
                  )}
                </Card>
              )}

              <p className="text-[10px] text-center pb-4" style={{ color: C.muted }}>
                Barème IR 2026 · Seuil Micro-BNC 2026 : {fmt(SEUIL_CA)} · Estimations indicatives — ne remplace pas un expert-comptable.
              </p>
            </div>
          );
        })()}

        {activeTab === "charges2035" && !isMicroBNC && (
          isEssentiel ? (
            <div className="max-w-lg mx-auto">
              <PaywallCard
                icon="📋"
                title="Onglet Charges 2035"
                description="Déduisez vos charges réelles et optimisez votre BNC classique."
                ctaLabel={proCta}
              />
            </div>
          ) : (
            <Charges2035 caAnnuel={stats.ca} onChargesSaved={loadChargesFromDB} />
          )
        )}
      </main>

      {/* ── MODAL SUCCÈS IMPORT ────────────────────────────────────────────── */}
      {showImportModal && importInsight && (() => {
        const { totalRecettes, totalCharges, nbRecettes } = importInsight;
        const cipav   = Math.round(totalRecettes * TAUX_URSSAF);
        const imposable = Math.round(totalRecettes * (1 - ABATTEMENT)); // 66% base imposable Micro-BNC
        const ir      = calculIR(imposable);
        const netAnn  = Math.max(0, totalRecettes - cipav - ir);
        const netMens = Math.round(netAnn / 12);
        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
            style={{ backgroundColor: "rgba(13,33,55,0.7)", backdropFilter: "blur(4px)" }}
            onClick={() => setShowImportModal(false)}
          >
            <div
              className="w-full max-w-md rounded-3xl p-6 space-y-5 shadow-2xl"
              style={{ backgroundColor: C.navyDark ?? "#0D2137" }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="text-center space-y-1">
                <div className="text-4xl mb-2">🎉</div>
                <p className="text-lg font-extrabold" style={{ color: "#FFFFFF" }}>
                  Analyse terminée
                </p>
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Nous avons identifié{" "}
                  <span className="font-bold" style={{ color: C.teal }}>
                    {fmt(totalRecettes)}
                  </span>{" "}
                  de recettes sur {nbRecettes} transaction{nbRecettes > 1 ? "s" : ""}.
                </p>
              </div>

              {/* Impact chiffres */}
              <div className="rounded-2xl p-4 space-y-3" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: C.teal }}>
                  Voici l'impact de ces chiffres sur votre année 2026
                </p>
                <div className="space-y-2">
                  {[
                    { label: "CA identifié",        value: fmt(totalRecettes), color: "#FFFFFF" },
                    { label: "CIPAV (23,4%)",        value: `− ${fmt(cipav)}`,  color: "#93C5FD" },
                    { label: "IR barème estimé",     value: `− ${fmt(ir)}`,     color: "#FCD34D" },
                    { label: "Vrai salaire net/an",  value: fmt(netAnn),        color: C.teal, bold: true },
                    { label: "Soit par mois",        value: fmt(netMens),       color: C.teal, bold: true },
                  ].map(({ label, value, color, bold }) => (
                    <div key={label} className="flex justify-between items-center">
                      <span className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{label}</span>
                      <span className={`text-sm ${bold ? "font-extrabold" : "font-semibold"}`} style={{ color }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alerte charges si pertinent */}
              {totalCharges > totalRecettes * ABATTEMENT && (
                <div className="rounded-xl px-3 py-2 text-xs" style={{ backgroundColor: "rgba(230,57,70,0.15)", color: "#FF8A8A" }}>
                  🚨 Vos charges ({Math.round(totalCharges / totalRecettes * 100)}% du CA) dépassent l'abattement forfaitaire 34%.
                  Le BNC réel pourrait être plus avantageux — consultez le Simulateur.
                </div>
              )}

              {/* CTAs */}
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowImportModal(false); setActiveTab("simulateur"); }}
                  className="flex-1 py-3 rounded-2xl text-xs font-bold border transition-opacity hover:opacity-80"
                  style={{ borderColor: C.teal, color: C.teal, backgroundColor: "transparent" }}
                >
                  🔄 Simuler Micro vs Réel
                </button>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="flex-1 py-3 rounded-2xl text-sm font-extrabold transition-opacity hover:opacity-90"
                  style={{ backgroundColor: C.teal, color: "#FFFFFF" }}
                >
                  Voir mon tableau de bord →
                </button>
              </div>

              <p className="text-center text-[10px]" style={{ color: "rgba(255,255,255,0.35)" }}>
                Calculs indicatifs — IR simplifié pour 1 part fiscale, avant déductions spécifiques.
              </p>
            </div>
          </div>
        );
      })()}

      <footer className="text-center py-6 pb-12 text-xs max-w-3xl mx-auto px-4" style={{ color: C.muted }}>
        {isMicroBNC ? DISCLAIMER_MICRO : DISCLAIMER_BNC}
      </footer>

      {/* Dev Debug Panel — DEV only */}
      <DevDebugPanel />
    </div>
  );
}
